import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatErr, resolveMediaUrl, API_BASE } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Plus,
  Pencil,
  Trash2,
  Stethoscope,
  Package,
  Building,
  Copy,
  Scissors,
  Megaphone,
  X,
  ShoppingCart,
  FolderTree,
  Images,
  ArrowUp,
  ArrowDown,
  GraduationCap,
  Search,
  Download,
  Printer,
  UserPlus,
  Settings,
  Save,
  MessageCircle,
  Share2,
  QrCode,
  Globe2,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { Checkbox } from "../../components/ui/checkbox";
import GeneralStorefrontPanel from "./GeneralStorefrontPanel";
import AdAgencyStorefrontPanel from "./AdAgencyStorefrontPanel";
import RetailStorefrontPanel from "./RetailStorefrontPanel";
import "./salon/salonWeb.css";
import HousingSocietyPanel from "./HousingSocietyPanel";
import { toast } from "sonner";
import {
  formatOrderDelivery,
  orderDeliverySearchText,
  orderFulfillmentType,
} from "../../lib/orderFulfillment";
import HotelRestaurantOps from "./HotelRestaurantOps";
import BasicMenuLevel2Orders from "./BasicMenuLevel2Orders";
import BasicMenuTableQrManager from "./BasicMenuTableQrManager";

const SALON_SERVICE_CATEGORIES = [
  "Hair & Styling",
  "Hair Colour & Treatments",
  "Skin & Facials",
  "Spa & Body",
  "Nails",
  "Makeup & Bridal",
  "Men's Grooming",
  "Packages & Memberships",
  "Kids",
  "Other services",
];

const SERVICE_PANEL_SECTIONS = {
  default: new Set(["dashboard", "bookings", "services", "inventory", "accounting", "settings"]),
  salon: new Set(["dashboard", "bookings", "services", "inventory", "accounting", "customers", "staff", "settings"]),
};

function ServicePanel({ vertical, label, icon: Icon }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const profileKey = `${vertical}_profile`;
  const emptyProfile = useMemo(
    () => ({
      specialty: "",
      qualifications: "",
      fee: 0,
      hours: "",
      is_open: true,
      image_url: "",
      description: "",
      slot_minutes: 15,
      services: [],
    }),
    [],
  );
  const [profile, setProfile] = useState(emptyProfile);
  const [apts, setApts] = useState([]);
  const [todayBookings, setTodayBookings] = useState([]);
  const [liveQueue, setLiveQueue] = useState([]);
  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);
  const [newBranchName, setNewBranchName] = useState("");
  const [me, setMe] = useState(null);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [serviceIndex, setServiceIndex] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    id: "",
    name: "",
    price: 0,
    duration_mins: 30,
    description: "",
    image_url: "",
    active: true,
    category: "",
    popular: false,
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    patient_name: "",
    patient_phone: "",
    preferred_time: "",
    booking_location: "",
    booking_device_id: "",
    service_id: "",
    service_ids: [],
    notes: "",
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [showPastBookings, setShowPastBookings] = useState(false);
  const [seoForm, setSeoForm] = useState({
    seo_title: "",
    meta_description: "",
    meta_keywords: "",
    canonical_url: "",
    og_image_url: "",
    business_address: "",
    google_map_url: "",
  });
  const [seoBusy, setSeoBusy] = useState(false);
  const [settingsTab, setSettingsTab] = useState("profile");
  const validSections = vertical === "salon" ? SERVICE_PANEL_SECTIONS.salon : SERVICE_PANEL_SECTIONS.default;
  const requestedSection = searchParams.get("section");
  // Each workspace section has a durable URL. This is deliberately derived
  // from the search parameter rather than a useState default so browser
  // refreshes always restore the exact Salon workspace section.
  const section = validSections.has(requestedSection) ? requestedSection : "dashboard";
  const selectSection = useCallback((nextSection) => {
    const next = validSections.has(nextSection) ? nextSection : "dashboard";
    const params = new URLSearchParams(searchParams);
    if (next === "dashboard") params.delete("section");
    else params.set("section", next);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams, validSections]);
  const [otpUsage, setOtpUsage] = useState(null);

  const title = label === "Salon" ? "Salon profile" : "Clinic profile";
  const bookingCta = label === "Salon" ? "Book service" : "Book appointment";
  const services = Array.isArray(profile.services) ? profile.services : [];
  const servingQueue = useMemo(
    () =>
      liveQueue
        .filter((item) => String(item.status || "").toLowerCase() === "called")
        .slice(0, 3),
    [liveQueue],
  );
  const visibleAppointments = useMemo(() => {
    if (showPastBookings) return apts;
    return todayBookings;
  }, [apts, showPastBookings, todayBookings]);
  const pastCount = Math.max(0, apts.length - todayBookings.length);

  const sortAppointments = useCallback((appointments = []) => {
    const rank = (status) =>
      ({ called: 0, pending: 1, done: 2, cancelled: 3 })[status || ""] ?? 9;
    return [...appointments].sort((a, b) => {
      const statusDelta = rank(a.status) - rank(b.status);
      if (statusDelta !== 0) return statusDelta;
      const tokenDelta = Number(a.token || 0) - Number(b.token || 0);
      if (tokenDelta !== 0) return tokenDelta;
      return String(a.created_at || "").localeCompare(
        String(b.created_at || ""),
      );
    });
  }, []);

  const load = useCallback(
    async (branchIdOverride = null) => {
      try {
        const branchId = branchIdOverride ?? selectedBranch?.id ?? "";
        const branchQuery = branchId
          ? `?branch_id=${encodeURIComponent(branchId)}`
          : "";
        const [m, board, d, br, seo] = await Promise.all([
          api.get("/client/me"),
          api.get(`/client/${vertical}/appointments/board${branchQuery}`),
          api.get(`/client/devices${branchQuery}`),
          api.get("/client/branches"),
          api.get("/client/seo-settings").catch(() => ({ data: {} })),
        ]);
        setMe(m.data);
        setSeoForm((prev) => ({ ...prev, ...(seo.data || {}) }));
        setApts(sortAppointments(board.data?.recent_bookings || []));
        setTodayBookings(sortAppointments(board.data?.today_bookings || []));
        setLiveQueue(sortAppointments(board.data?.live_queue || []));
        setDevices(Array.isArray(d.data) ? d.data : []);
        const uniqueBranches = Array.from(
          new Map(
            (Array.isArray(br.data) ? br.data : [])
              .filter(Boolean)
              .map((branch) => [branch.id, branch]),
          ).values(),
        );
        setBranches(uniqueBranches);
        const branchInState = selectedBranch?.id
          ? uniqueBranches.find((branch) => branch.id === selectedBranch.id) ||
            null
          : null;
        const nextBranch =
          branchInState || (selectedBranch?.id ? null : selectedBranch);

        const clientProfile = m.data?.[profileKey] || {};
        const branchProfileType = nextBranch?.profile_type || "";
        const branchProfileRaw = nextBranch?.profile || {};
        const branchProfile =
          branchProfileType && branchProfileType !== profileKey
            ? {}
            : branchProfileRaw;
        const mergedProfile = branchId
          ? { ...emptyProfile, ...clientProfile, ...branchProfile }
          : { ...emptyProfile, ...clientProfile };
        setProfile({
          ...mergedProfile,
          services: Array.isArray(mergedProfile.services)
            ? mergedProfile.services
            : [],
        });
      } catch {}
    },
    [profileKey, vertical, emptyProfile, sortAppointments, selectedBranch],
  );
  useEffect(() => {
    setProfile(emptyProfile);
    setApts([]);
    setTodayBookings([]);
    setLiveQueue([]);
    setSelectedBranch(null);
  }, [vertical, profileKey, emptyProfile]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (vertical !== "salon" || section !== "customers") return;
    api.get("/client/salon/otp-usage").then(({ data }) => setOtpUsage(data)).catch((e) => toast.error(formatErr(e.response?.data?.detail)));
  }, [vertical, section]);

  const save = async () => {
    try {
      const branchQuery = activeBranchId
        ? `?branch_id=${encodeURIComponent(activeBranchId)}`
        : "";
      await api.put(`/client/${vertical}/profile${branchQuery}`, profile);
      toast.success(`${label} profile saved`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const saveSeoSettings = async () => {
    setSeoBusy(true);
    try {
      const { data } = await api.put("/client/seo-settings", seoForm);
      setSeoForm((prev) => ({ ...prev, ...(data || {}) }));
      toast.success("SEO settings saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSeoBusy(false);
    }
  };

  const setStatus = async (
    aid,
    status,
    { recall = false, undoCall = false, token = "" } = {},
  ) => {
    try {
      await api.post(
        `/client/${vertical}/appointments/${aid}/status?status=${status}`,
      );
      if (recall) {
        toast.success(
          `Recall sent for token ${token}. The queue display will announce it again.`,
        );
      } else if (undoCall) {
        toast.success(`Token ${token} was returned to the waiting queue.`);
      } else if (status === "called") {
        toast.success(`Token ${token} is now being called.`);
      }
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const undoCall = (appointment) => {
    const token = appointment.token || "this token";
    if (
      !window.confirm(
        `Return token ${token} to the waiting queue? Its number will stay the same.`,
      )
    ) {
      return;
    }
    setStatus(appointment.id, "pending", { undoCall: true, token });
  };

  const cancelAppointment = (appointment) => {
    const token = appointment.token || "this booking";
    if (
      !window.confirm(
        `Cancel token ${token} for ${appointment.patient_name || "this customer"}? This removes it from the live queue.`,
      )
    ) {
      return;
    }
    setStatus(appointment.id, "cancelled", { token });
  };

  const openServiceEditor = (item = null, index = null) => {
    setServiceIndex(index);
    setServiceForm(
      item
        ? {
            id: item.id || `svc-${Date.now()}`,
            name: item.name || "",
            price: item.price || 0,
            duration_mins: item.duration_mins || 30,
            description: item.description || "",
            image_url: item.image_url || "",
            active: item.active !== false,
            category: item.category || "",
            popular: !!item.popular,
            tags: Array.isArray(item.tags)
              ? item.tags.join(", ")
              : item.tags || "",
          }
        : {
            id: `svc-${Date.now()}`,
            name: "",
            price: 0,
            duration_mins: 30,
            description: "",
            image_url: "",
            active: true,
            category: "",
            popular: false,
            tags: "",
          },
    );
    setServiceOpen(true);
  };

  const saveService = (e) => {
    e.preventDefault();
    const next = [...services];
    const payload = {
      ...serviceForm,
      price: Number(serviceForm.price || 0),
      duration_mins: Math.max(5, parseInt(serviceForm.duration_mins || 30, 10)),
      active: !!serviceForm.active,
    };
    // normalize tags to array on save
    if (typeof payload.tags === "string")
      payload.tags = payload.tags
        .split(/[;,|]/)
        .map((t) => t.trim())
        .filter(Boolean);
    if (serviceIndex === null) next.push(payload);
    else
      next[serviceIndex] = {
        ...(next[serviceIndex] || {}),
        ...payload,
        id: next[serviceIndex]?.id || payload.id,
      };
    const nextProfile = { ...profile, services: next };
    setProfile(nextProfile);
    setServiceOpen(false);
    api
      .put(
        `/client/${vertical}/profile${activeBranchId ? `?branch_id=${encodeURIComponent(activeBranchId)}` : ""}`,
        nextProfile,
      )
      .then(() => {
        toast.success(
          serviceIndex === null
            ? `${label} service added`
            : `${label} service updated`,
        );
        load();
      })
      .catch((e) => {
        toast.error(formatErr(e.response?.data?.detail));
      });
  };

  const removeService = (index) => {
    const next = services.filter((_, i) => i !== index);
    const nextProfile = { ...profile, services: next };
    setProfile(nextProfile);
    api
      .put(
        `/client/${vertical}/profile${activeBranchId ? `?branch_id=${encodeURIComponent(activeBranchId)}` : ""}`,
        nextProfile,
      )
      .then(() => {
        toast.success(`${label} service removed`);
        load();
      })
      .catch((e) => {
        toast.error(formatErr(e.response?.data?.detail));
      });
  };

  const uploadServiceImage = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "storefront");
      fd.append("zone", "main");
      fd.append("name", file.name);
      const { data } = await api.post("/client/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setServiceForm((prev) => ({
        ...prev,
        image_url: data.public_url || `${API_BASE}${data.url || ""}`,
      }));
      toast.success("Service image uploaded");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setUploadingImage(false);
    }
  };

  const uploadCoverImage = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "storefront");
      fd.append("zone", "main");
      fd.append("name", "cover");
      const { data } = await api.post("/client/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newProfile = {
        ...profile,
        image_url:
          data.public_url ||
          (data.url ? `${API_BASE}${data.url}` : profile.image_url),
      };
      setProfile(newProfile);
      // persist immediately
      await api.put(
        `/client/${vertical}/profile${activeBranchId ? `?branch_id=${encodeURIComponent(activeBranchId)}` : ""}`,
        newProfile,
      );
      toast.success("Cover image uploaded and saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setUploadingCover(false);
    }
  };

  const openManualBooking = () => {
    const firstServiceId = services[0]?.id || "";
    const firstDevice = devices[0] || null;
    setManualForm({
      patient_name: "",
      patient_phone: "",
      preferred_time: "",
      booking_location: firstDevice?.location || "",
      booking_device_id: firstDevice?.id || "",
      service_id: firstServiceId,
      service_ids: firstServiceId ? [firstServiceId] : [],
      notes: "",
    });
    setManualOpen(true);
  };

  const submitManualBooking = async (e) => {
    e.preventDefault();
    try {
      const selected = services.filter((item) =>
        manualForm.service_ids.includes(item.id),
      );
      const fallback = selected.length
        ? selected
        : services.find((item) => item.id === manualForm.service_id)
          ? [services.find((item) => item.id === manualForm.service_id)]
          : services[0]
            ? [services[0]]
            : [];
      await api.post(
        `/client/${vertical}/appointments${activeBranchId ? `?branch_id=${encodeURIComponent(activeBranchId)}` : ""}`,
        {
          patient_name: manualForm.patient_name,
          patient_phone: manualForm.patient_phone,
          preferred_time: manualForm.preferred_time,
          booking_location: manualForm.booking_location || "",
          booking_device_id: manualForm.booking_device_id || "",
          notes: manualForm.notes,
          service_id: fallback[0]?.id || manualForm.service_id || "",
          service_ids: fallback.map((item) => item.id).filter(Boolean),
          service_name:
            fallback
              .map((item) => item?.name)
              .filter(Boolean)
              .join(" + ") || "Walk-in booking",
          service_price: fallback.reduce(
            (sum, item) => sum + Number(item?.price || 0),
            0,
          ),
          source: "manual",
        },
      );
      toast.success("Walk-in booking added");
      setManualOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const bookingSlug = useMemo(() => {
    if (!me) return "";
    const base = String(me.public_booking_slug || me.name || me.email || "")
      .toLowerCase()
      .replace(/@/g, "-")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48);
    return me.public_booking_slug || me.id || me.client_code || base || "";
  }, [me]);
  const bookingUrl = useMemo(() => {
    const branchQuery = selectedBranch?.id
      ? `?branch_id=${encodeURIComponent(selectedBranch.id)}`
      : "";
    if (
      me?.custom_booking_domain_status === "active" &&
      me?.custom_booking_domain
    ) {
      return `https://${me.custom_booking_domain}${branchQuery}`;
    }
    const ref = bookingSlug || me?.public_booking_slug || me?.id || "";
    if (!ref) return "";
    const base = `${window.location.origin}/book/${encodeURIComponent(ref)}`;
    return `${base}${branchQuery}`;
  }, [
    bookingSlug,
    me?.public_booking_slug,
    me?.id,
    me?.custom_booking_domain,
    me?.custom_booking_domain_status,
    selectedBranch,
  ]);
  const standardBookingUrl = useMemo(() => {
    const ref = bookingSlug || me?.public_booking_slug || me?.id || "";
    if (!ref) return "";
    const branchQuery = selectedBranch?.id
      ? `?branch_id=${encodeURIComponent(selectedBranch.id)}`
      : "";
    return `${window.location.origin}/book/${encodeURIComponent(ref)}${branchQuery}`;
  }, [bookingSlug, me?.public_booking_slug, me?.id, selectedBranch]);
  const customDomainStatus = String(
    me?.custom_booking_domain_status || "none",
  ).toLowerCase();
  const customDomainActive =
    customDomainStatus === "active" && !!me?.custom_booking_domain;
  const copyValue = async (value, message) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error("Unable to copy link");
    }
  };
  const activeBranchId = useMemo(() => {
    const selectedId = selectedBranch?.id || "";
    if (selectedId) return selectedId;
    const selectedName = String(selectedBranch?.name || "")
      .trim()
      .toLowerCase();
    if (selectedName) {
      const found = branches.find(
        (branch) =>
          String(branch.name || "")
            .trim()
            .toLowerCase() === selectedName,
      );
      if (found?.id) return found.id;
    }
    return "";
  }, [branches, selectedBranch]);
  const copyLink = async () => {
    await copyValue(bookingUrl, "Booking link copied");
  };

  const createBranch = async () => {
    try {
      const payload = { name: newBranchName || `Branch ${Date.now()}` };
      const { data } = await api.post("/client/branches", payload);
      setBranchDialogOpen(false);
      setNewBranchName("");
      toast.success("Branch created");
      if (data?.id) setSelectedBranch(data);
      load(data?.id || null);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const deleteBranch = async () => {
    if (!selectedBranch?.id) return;
    if (
      !window.confirm(
        `Delete branch "${selectedBranch.name || selectedBranch.id}"? This removes branch-specific devices, bookings and playlists.`,
      )
    )
      return;
    try {
      await api.delete(
        `/client/branches/${encodeURIComponent(selectedBranch.id)}`,
      );
      toast.success("Branch deleted");
      setSelectedBranch(null);
      load(null);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  return (
    <div
      className={`${vertical === "salon" ? "salon-web-shell" : ""} space-y-4 font-sans`}
    >
      <div className="border border-[#D1D5DB] bg-[#F9FAFB] px-3 py-2 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <div className="mr-auto flex items-center gap-2">
            <Scissors className="h-4 w-4 text-[#6B7280]" />
            <span className="text-sm font-bold">Salon workspace</span>
            <span className="border-l pl-2 text-xs text-[#6B7280]">
              {selectedBranch?.name || "Global"}
            </span>
          </div>
          <select
            value={selectedBranch?.id || ""}
            onChange={(e) => {
              const bid = e.target.value;
              const found = branches.find((b) => b.id === bid);
              setSelectedBranch(found || null);
              load(found?.id || null);
            }}
            className="h-8 min-w-[170px] rounded-sm border border-[#D1D5DB] bg-white px-2 text-xs text-[#111827]"
          >
            <option value="">Global</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBranchDialogOpen(true)}
            className="h-8 rounded-sm"
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Branch
          </Button>
          {selectedBranch?.id ? (
            <Button
              size="sm"
              variant="outline"
              onClick={deleteBranch}
              className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      <div className="salon-primary-nav overflow-x-auto border border-[#D1D5DB] bg-white shadow-sm">
        <div className="flex min-w-max">
          {[
            ["dashboard", "Dashboard"],
            ["bookings", "Bookings & queue"],
            ["services", "Service rate card"],
            ["inventory", "Inventory"],
            ["accounting", "Accounting"],
            ...(vertical === "salon" ? [["customers", "Customers & OTP"]] : []),
            ...(vertical === "salon" ? [["staff", "Staff access"]] : []),
            ["settings", "Storefront & domain"],
          ].map(([key, caption]) => (
            <button
              key={key}
              type="button"
              onClick={() => selectSection(key)}
              className={`border-r border-[#E5E7EB] px-4 py-2.5 text-xs font-semibold transition ${section === key ? "bg-[#111827] text-white" : "bg-white text-[#374151] hover:bg-[#F3F4F6]"}`}
            >
              {caption}
            </button>
          ))}
        </div>
      </div>

      {section === "dashboard" ? (
        <>
          <div className="grid grid-cols-2 border border-[#D1D5DB] bg-white sm:grid-cols-4">
            {[
              [
                "Waiting",
                liveQueue.filter((item) => item.status === "pending").length,
              ],
              ["Serving", servingQueue.length],
              [
                "Completed",
                todayBookings.filter((item) => item.status === "done").length,
              ],
              ["Today", todayBookings.length],
            ].map(([caption, value], index) => (
              <div
                key={caption}
                className={`px-3 py-2 ${index ? "border-l border-[#E5E7EB]" : ""} ${index > 1 ? "border-t sm:border-t-0" : ""}`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">
                  {caption}
                </div>
                <div className="mt-0.5 text-xl font-bold tabular-nums text-[#111827]">
                  {value}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-px border border-[#D1D5DB] bg-[#D1D5DB] md:grid-cols-4">
            {[
              ["bookings", "Open booking register"],
              ["services", "Edit rate card"],
              ["inventory", "Check stock"],
              ["accounting", "Open accounts"],
            ].map(([target, caption]) => (
              <button
                key={target}
                type="button"
                onClick={() => selectSection(target)}
                className="bg-white px-3 py-3 text-left text-xs font-semibold hover:bg-[#F9FAFB]"
              >
                {caption} →
              </button>
            ))}
          </div>
        </>
      ) : null}

      {section === "bookings" ? (
        <>
          {/* Bookings and tokens (moved to top) */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
            <div className="salon-section-bar px-3 py-2 border-b border-[#E5E7EB] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] mb-1">
                  Live queue
                </div>
                <h3 className="font-display text-base font-bold tracking-tight">
                  Bookings and tokens
                </h3>
                <div className="mt-1 text-xs text-[#6B7280]">
                  Live queue: {liveQueue.length} · Currently serving:{" "}
                  {servingQueue.length} · Today's bookings:{" "}
                  {todayBookings.length}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => setShowPastBookings((value) => !value)}
                >
                  {showPastBookings
                    ? "Show today only"
                    : `Show past bookings${pastCount ? ` (${pastCount})` : ""}`}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-sm"
                  onClick={openManualBooking}
                >
                  Add walk-in
                </Button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                    Token
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                    Branch
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                    Name
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                    Phone
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                    Time
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                    Status
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apts.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-10 text-sm text-[#6B7280]"
                    >
                      No bookings yet. Share your link to start receiving
                      tokens.
                    </TableCell>
                  </TableRow>
                )}
                {apts.length > 0 &&
                  visibleAppointments.length === 0 &&
                  !showPastBookings && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-10 text-sm text-[#6B7280]"
                      >
                        No bookings for today. Toggle past bookings to view
                        older queue items.
                      </TableCell>
                    </TableRow>
                  )}
                {visibleAppointments.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-[#111827] text-white font-mono font-bold">
                        {a.token}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center rounded-full bg-[#F3F4F6] px-2.5 py-1 text-[11px] uppercase tracking-wider text-[#374151] font-semibold">
                        {a.branch_name || selectedBranch?.name || "Global"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold">{a.patient_name}</div>
                      <div className="text-xs text-[#6B7280]">
                        {a.service_name || bookingCta}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {a.patient_phone}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {a.assigned_time || a.preferred_time || "ASAP"}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-full px-2 py-1 text-[11px] uppercase tracking-wider font-semibold">
                        {a.status === "called" ? "Currently serving" : a.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {a.status === "pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm mr-2"
                          onClick={() =>
                            setStatus(a.id, "called", { token: a.token })
                          }
                        >
                          Call
                        </Button>
                      )}
                      {a.status === "called" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm mr-2"
                          title="Repeat the queue announcement for this customer"
                          onClick={() =>
                            setStatus(a.id, "called", {
                              recall: true,
                              token: a.token,
                            })
                          }
                        >
                          Recall customer
                        </Button>
                      )}
                      {a.status === "called" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm mr-2"
                          title="Return this customer to the waiting queue"
                          onClick={() => undoCall(a)}
                        >
                          Undo call
                        </Button>
                      )}
                      {a.status === "called" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm mr-2"
                          onClick={() => setStatus(a.id, "done")}
                        >
                          Done
                        </Button>
                      )}
                      {a.status !== "cancelled" && a.status !== "done" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm text-red-600"
                          onClick={() => cancelAppointment(a)}
                        >
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      ) : null}

      {section === "services" ? (
        <>
          {/* Services and pricing (middle) */}
          <div className="bg-white border border-[#D1D5DB] rounded-sm overflow-hidden shadow-sm">
            <div className="salon-section-bar px-3 py-2 border-b border-[#E5E7EB] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-[#F9FAFB]">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] mb-1">
                  Catalog
                </div>
                <h3 className="font-display text-base font-bold tracking-tight">
                  Services and pricing
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="rounded-sm"
                  onClick={openManualBooking}
                >
                  Add walk-in
                </Button>
                <Button
                  className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
                  onClick={() => openServiceEditor()}
                >
                  Add service
                </Button>
              </div>
            </div>
            <div className="p-2 sm:p-3">
              {services.length === 0 ? (
                <div className="border border-dashed border-[#D1D5DB] rounded-sm p-5 text-center text-xs text-[#6B7280]">
                  No services yet. Add one premium service row for your
                  storefront.
                </div>
              ) : (
                <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                        <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                          Service
                        </TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280] text-right">
                          Price
                        </TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                          Visibility
                        </TableHead>
                        <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                          Image
                        </TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {services.map((service, index) => (
                        <TableRow key={service.id || index}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-sm bg-[#111827] overflow-hidden shrink-0">
                                {service.image_url ? (
                                  <img
                                    src={service.image_url}
                                    alt={service.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-[linear-gradient(135deg,#111827,#334155)]" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold truncate">
                                  {service.name || "Service"}
                                </div>
                                <div className="text-xs text-[#6B7280] line-clamp-1">
                                  {service.description ||
                                    "Premium service item for your storefront."}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            ₹{Number(service.price || 0).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`text-[11px] uppercase tracking-wider font-semibold ${service.active === false ? "text-[#9CA3AF]" : "text-emerald-600"}`}
                            >
                              {service.active === false ? "Hidden" : "Visible"}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-[#6B7280]">
                            {service.image_url ? "Added" : "No image"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              className="rounded-sm mr-1"
                              onClick={() => openServiceEditor(service, index)}
                            >
                              <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                            </Button>
                            <Button
                              variant="outline"
                              className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => removeService(index)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}

      {section === "inventory" ? <SalonInventoryPanel /> : null}
      {section === "accounting" ? (
        <SalonAccountingPanel appointments={apts} />
      ) : null}
      {section === "customers" ? (
        <section className="border border-[#D1D5DB] bg-white shadow-sm">
          <div className="flex items-center justify-between border-b bg-[#F9FAFB] px-3 py-2">
            <div><div className="text-sm font-bold">Customer verification & OTP usage</div><div className="text-[11px] text-[#6B7280]">Delivered WhatsApp OTPs are billed at ₹0.25 each</div></div>
            <span className="text-xs font-semibold">{otpUsage?.month || "Current month"}</span>
          </div>
          <div className="grid grid-cols-2 border-b sm:grid-cols-5">
            {[["Requested", otpUsage?.requested || 0], ["Delivered", otpUsage?.delivered || 0], ["Verified", otpUsage?.verified || 0], ["Failed", otpUsage?.failed || 0], ["Payable", `₹${Number(otpUsage?.billable_amount || 0).toFixed(2)}`]].map(([caption, value]) => <div key={caption} className="border-r px-3 py-2"><div className="text-[9px] font-semibold uppercase tracking-wider text-[#6B7280]">{caption}</div><div className="text-lg font-bold tabular-nums">{value}</div></div>)}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-xs"><thead className="bg-[#F3F4F6] text-left text-[10px] uppercase tracking-wider text-[#6B7280]"><tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Mobile</th><th className="px-3 py-2">Delivery</th><th className="px-3 py-2">Verification</th><th className="px-3 py-2 text-right">Charge</th></tr></thead><tbody>{(otpUsage?.recent || []).map((row) => <tr key={row.id} className="border-t"><td className="px-3 py-2">{row.created_at ? new Date(row.created_at).toLocaleString() : "-"}</td><td className="px-3 py-2 font-mono">{row.phone || "••••"}</td><td className="px-3 py-2 capitalize">{row.status || "sent"}</td><td className="px-3 py-2">{row.verified ? "Verified" : "Pending"}</td><td className="px-3 py-2 text-right tabular-nums">₹{Number(row.billableAmount || 0).toFixed(2)}</td></tr>)}{!otpUsage?.recent?.length ? <tr><td colSpan="5" className="px-3 py-10 text-center text-[#6B7280]">No OTP usage this month.</td></tr> : null}</tbody></table>
          </div>
        </section>
      ) : null}

      {section === "staff" ? <SalonStaffPanel /> : null}

      {section === "settings" ? (
        <>
          {/* Public storefront / profile (moved to bottom) */}
          <div className="bg-gradient-to-br from-[#111827] via-[#1F2937] to-[#0F172A] text-white rounded-2xl p-4 sm:p-6 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.9)] border border-white/10">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/60 mb-2">
                  {title}
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
                  Public storefront
                </h3>
                <p className="mt-3 text-sm text-white/70 max-w-xl">
                  Add services with prices and images. The system manages live
                  tokens, queue status, and smart waiting-time estimates
                  automatically.
                </p>
                <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs text-white/80">
                  <span className="uppercase tracking-[0.2em] text-white/50">
                    Active branch
                  </span>
                  <span className="font-semibold text-white">
                    {selectedBranch?.name || "Global storefront"}
                  </span>
                  {selectedBranch?.id ? (
                    <span className="text-white/50">
                      · {selectedBranch.id.slice(0, 8)}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-2xl px-4 py-3 border border-white/10">
                <Label className="text-xs text-white/70">
                  Accepting bookings
                </Label>
                <Switch
                  checked={!!profile.is_open}
                  onCheckedChange={(v) =>
                    setProfile({ ...profile, is_open: v })
                  }
                  data-testid="toggle-open"
                />
              </div>
            </div>

            <Tabs
              value={settingsTab}
              onValueChange={setSettingsTab}
              className="mt-6"
            >
              <TabsList className="bg-white/10 border border-white/10 p-1 h-auto flex flex-wrap justify-start">
                <TabsTrigger
                  value="profile"
                  className="rounded-md px-4 py-2 text-white data-[state=active]:bg-white data-[state=active]:text-[#111827]"
                >
                  Public settings
                </TabsTrigger>
                <TabsTrigger
                  value="seo"
                  className="rounded-md px-4 py-2 text-white data-[state=active]:bg-white data-[state=active]:text-[#111827]"
                >
                  SEO settings
                </TabsTrigger>
                <TabsTrigger
                  value="link"
                  className="rounded-md px-4 py-2 text-white data-[state=active]:bg-white data-[state=active]:text-[#111827]"
                >
                  Booking link
                </TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="mt-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <Label className="text-xs uppercase tracking-wider text-white/60">
                      Headline
                    </Label>
                    <Input
                      value={profile.specialty}
                      onChange={(e) =>
                        setProfile({ ...profile, specialty: e.target.value })
                      }
                      className="rounded-lg bg-white/95 text-[#111827]"
                      placeholder={
                        label === "Salon"
                          ? "Premium haircuts, styling, beard trim"
                          : "General medicine, dental, diagnostics"
                      }
                    />
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <Label className="text-xs uppercase tracking-wider text-white/60">
                      Featured price
                    </Label>
                    <Input
                      type="number"
                      value={profile.fee}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          fee: parseFloat(e.target.value || 0),
                        })
                      }
                      className="rounded-lg bg-white/95 text-[#111827]"
                    />
                  </div>
                  <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-xl p-4">
                    <Label className="text-xs uppercase tracking-wider text-white/60">
                      Details
                    </Label>
                    <Input
                      value={profile.qualifications}
                      onChange={(e) =>
                        setProfile({
                          ...profile,
                          qualifications: e.target.value,
                        })
                      }
                      className="rounded-lg bg-white/95 text-[#111827]"
                      placeholder={
                        label === "Salon"
                          ? "Senior stylist, bridal specialist"
                          : "MBBS, MD"
                      }
                    />
                  </div>
                  <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-xl p-4">
                    <Label className="text-xs uppercase tracking-wider text-white/60">
                      Description
                    </Label>
                    <Textarea
                      value={profile.description}
                      onChange={(e) =>
                        setProfile({ ...profile, description: e.target.value })
                      }
                      className="rounded-lg bg-white/95 text-[#111827]"
                      rows={3}
                      placeholder="Write a premium description for end users."
                    />
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <Label className="text-xs uppercase tracking-wider text-white/60">
                      Working hours
                    </Label>
                    <Input
                      value={profile.hours}
                      onChange={(e) =>
                        setProfile({ ...profile, hours: e.target.value })
                      }
                      placeholder="Mon-Sat · 9 AM - 7 PM"
                      className="rounded-lg bg-white/95 text-[#111827]"
                    />
                  </div>
                  <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-xl p-4">
                    <Label className="text-xs uppercase tracking-wider text-white/60">
                      Cover image
                    </Label>
                    <div className="mt-3 flex items-center gap-4">
                      <div className="w-28 h-20 rounded-lg overflow-hidden bg-[#0F172A] flex items-center justify-center">
                        {profile.image_url ? (
                          // preview
                          <img
                            src={profile.image_url}
                            alt="cover"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-xs text-white/60 px-2">
                            No cover image
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label
                          className={`inline-flex items-center gap-2 rounded-md px-3 py-2 bg-white/10 border border-white/10 text-sm cursor-pointer ${uploadingCover ? "opacity-60 pointer-events-none" : ""}`}
                        >
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                              uploadCoverImage(
                                e.target.files && e.target.files[0],
                              )
                            }
                            className="hidden"
                          />
                          <span>
                            {uploadingCover ? "Uploading…" : "Upload image"}
                          </span>
                        </label>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setProfile({ ...profile, image_url: "" });
                              api
                                .put(`/client/${vertical}/profile`, {
                                  ...profile,
                                  image_url: "",
                                })
                                .catch(() => {});
                            }}
                            className="rounded-xl"
                          >
                            Remove
                          </Button>
                          <div className="text-xs text-white/60 self-center">
                            Recommended: 1200×600px JPG/PNG
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent
                value="seo"
                className="mt-5 rounded-xl border border-white/10 bg-white/5 p-4"
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Settings className="h-4 w-4" /> SEO settings
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    value={seoForm.seo_title || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, seo_title: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="Meta title"
                  />
                  <Input
                    value={seoForm.meta_keywords || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, meta_keywords: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="Meta keywords"
                  />
                  <Textarea
                    value={seoForm.meta_description || ""}
                    onChange={(e) =>
                      setSeoForm({
                        ...seoForm,
                        meta_description: e.target.value,
                      })
                    }
                    className="rounded-lg bg-white/95 text-[#111827] md:col-span-2"
                    rows={2}
                    placeholder="Meta description"
                  />
                  <Input
                    value={seoForm.canonical_url || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, canonical_url: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="Canonical URL"
                  />
                  <Input
                    value={seoForm.og_image_url || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, og_image_url: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="OG image URL"
                  />
                  <Input
                    value={seoForm.business_address || ""}
                    onChange={(e) =>
                      setSeoForm({
                        ...seoForm,
                        business_address: e.target.value,
                      })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="Business address"
                  />
                  <Input
                    value={seoForm.google_map_url || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, google_map_url: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="Google Map URL"
                  />
                  <Button
                    type="button"
                    onClick={saveSeoSettings}
                    disabled={seoBusy}
                    className="rounded-xl bg-white text-[#111827] hover:bg-white/90 md:col-span-2"
                  >
                    {seoBusy ? "Saving SEO..." : "Save SEO settings"}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="link" className="mt-5">
                <div className="overflow-hidden rounded-sm border border-white/15 bg-white text-[#111827]">
                  <div className="grid grid-cols-1 divide-y divide-[#E5E7EB] lg:grid-cols-[170px_1fr_auto] lg:divide-x lg:divide-y-0">
                    <div className="bg-[#F3F4F6] px-3 py-2">
                      <div className="flex items-center gap-2 text-xs font-bold">
                        <Globe2 className="h-4 w-4" /> Primary booking URL
                      </div>
                      <div className="mt-1 text-[10px] text-[#6B7280]">
                        Share this with customers
                      </div>
                    </div>
                    <div className="min-w-0 px-3 py-2 font-mono text-xs break-all">
                      {bookingUrl || "Loading booking link..."}
                    </div>
                    <div className="flex gap-1 p-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={copyLink}
                        disabled={!bookingUrl}
                        className="h-8 rounded-sm"
                      >
                        <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          bookingUrl &&
                          window.open(
                            bookingUrl,
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                        disabled={!bookingUrl}
                        className="h-8 rounded-sm"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  <div className="border-t border-[#E5E7EB]">
                    <div className="grid grid-cols-1 lg:grid-cols-[170px_1fr]">
                      <div className="border-b border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 lg:border-b-0 lg:border-r">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">
                          Platform fallback
                        </div>
                      </div>
                      <div className="flex min-w-0 items-center gap-2 px-3 py-2">
                        <span className="min-w-0 flex-1 break-all font-mono text-xs text-[#4B5563]">
                          {standardBookingUrl || "—"}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            copyValue(
                              standardBookingUrl,
                              "Fallback link copied",
                            )
                          }
                          className="h-7 shrink-0 rounded-sm px-2"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-[#E5E7EB] bg-[#F9FAFB] p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {customDomainActive ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                        )}
                        <div>
                          <div className="text-xs font-bold">
                            Custom domain ·{" "}
                            {customDomainActive
                              ? "Active"
                              : customDomainStatus === "none"
                                ? "Not connected"
                                : customDomainStatus.replace(/_/g, " ")}
                          </div>
                          <div className="text-[11px] text-[#6B7280]">
                            {me?.custom_booking_domain
                              ? me.custom_booking_domain
                              : "Premium add-on: ask your administrator to activate your domain."}
                          </div>
                        </div>
                      </div>
                      {me?.custom_booking_domain && !customDomainActive ? (
                        <div className="rounded-sm border border-[#FCD34D] bg-[#FFFBEB] px-2 py-1 text-[10px] text-[#92400E]">
                          CNAME target: booking.rpsignage.com
                        </div>
                      ) : null}
                    </div>
                    {me?.custom_booking_domain_error ? (
                      <div className="mt-2 text-[11px] text-amber-700">
                        {me.custom_booking_domain_error}
                      </div>
                    ) : null}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </>
      ) : null}

      <Dialog open={serviceOpen} onOpenChange={setServiceOpen}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
              {serviceIndex === null ? "Add service" : "Edit service"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveService} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Service name
              </Label>
              <Input
                value={serviceForm.name}
                onChange={(e) =>
                  setServiceForm({ ...serviceForm, name: e.target.value })
                }
                required
                className="rounded-sm"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Price (₹)
              </Label>
              <Input
                type="number"
                value={serviceForm.price}
                onChange={(e) =>
                  setServiceForm({
                    ...serviceForm,
                    price: parseFloat(e.target.value || 0),
                  })
                }
                className="rounded-sm"
              />
            </div>
            {vertical === "salon" ? (
              <>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                    Service category
                  </Label>
                  <Input
                    list="salon-service-categories"
                    value={serviceForm.category || ""}
                    onChange={(e) => setServiceForm({ ...serviceForm, category: e.target.value })}
                    className="rounded-sm"
                    placeholder="Choose a standard category or type a new one"
                  />
                  <datalist id="salon-service-categories">
                    {SALON_SERVICE_CATEGORIES.map((category) => <option key={category} value={category} />)}
                  </datalist>
                  <p className="mt-1 text-[11px] text-[#6B7280]">Type any additional category when your salon needs one.</p>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Switch checked={!!serviceForm.popular} onCheckedChange={(v) => setServiceForm({ ...serviceForm, popular: v })} />
                  <Label className="text-sm">Show first in customer Popular services</Label>
                </div>
              </>
            ) : null}
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Description
              </Label>
              <Textarea
                value={serviceForm.description}
                onChange={(e) =>
                  setServiceForm({
                    ...serviceForm,
                    description: e.target.value,
                  })
                }
                className="rounded-sm"
                rows={3}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Tags (comma separated)
              </Label>
              <Input
                value={serviceForm.tags || ""}
                onChange={(e) =>
                  setServiceForm({ ...serviceForm, tags: e.target.value })
                }
                className="rounded-sm"
                placeholder="pillows, bedding, cotton"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Service image
              </Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => uploadServiceImage(e.target.files?.[0])}
                className="rounded-sm"
              />
              <div className="mt-2 text-xs text-[#6B7280]">
                Uploads into your storefront folder in media.
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Image URL
              </Label>
              <Input
                value={serviceForm.image_url}
                onChange={(e) =>
                  setServiceForm({ ...serviceForm, image_url: e.target.value })
                }
                className="rounded-sm"
                placeholder="https://..."
              />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch
                checked={!!serviceForm.active}
                onCheckedChange={(v) =>
                  setServiceForm({ ...serviceForm, active: v })
                }
              />
              <Label className="text-sm">Visible on public storefront</Label>
            </div>
            <DialogFooter className="col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setServiceOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
              >
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={branchDialogOpen} onOpenChange={setBranchDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
              Create branch
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createBranch();
            }}
            className="space-y-4"
          >
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Branch name
              </Label>
              <Input
                value={newBranchName}
                onChange={(e) => setNewBranchName(e.target.value)}
                required
                className="rounded-sm"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setBranchDialogOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
              >
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="rounded-2xl max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
              Add walk-in booking
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitManualBooking} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Customer name
              </Label>
              <Input
                value={manualForm.patient_name}
                onChange={(e) =>
                  setManualForm({ ...manualForm, patient_name: e.target.value })
                }
                required
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Phone
              </Label>
              <Input
                value={manualForm.patient_phone}
                onChange={(e) =>
                  setManualForm({
                    ...manualForm,
                    patient_phone: e.target.value,
                  })
                }
                required
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Preferred time
              </Label>
              <Input
                value={manualForm.preferred_time}
                onChange={(e) =>
                  setManualForm({
                    ...manualForm,
                    preferred_time: e.target.value,
                  })
                }
                placeholder="ASAP"
                className="rounded-sm"
              />
            </div>
            {devices.length > 0 ? (
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Location
                </Label>
                <select
                  value={manualForm.booking_location || ""}
                  onChange={(e) =>
                    setManualForm({
                      ...manualForm,
                      booking_location: e.target.value,
                      booking_device_id: "",
                    })
                  }
                  className="w-full h-10 rounded-sm border border-[#D1D5DB] bg-white px-3 text-sm"
                >
                  <option value="">Default location</option>
                  {[
                    ...new Set(
                      devices
                        .map((device) => String(device.location || "").trim())
                        .filter(Boolean),
                    ),
                  ].map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {devices.length > 0 ? (
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Route to device
                </Label>
                <select
                  value={manualForm.booking_device_id || ""}
                  onChange={(e) =>
                    setManualForm({
                      ...manualForm,
                      booking_device_id: e.target.value,
                    })
                  }
                  className="w-full h-10 rounded-sm border border-[#D1D5DB] bg-white px-3 text-sm"
                >
                  <option value="">Auto-select device</option>
                  {devices
                    .filter(
                      (device) =>
                        !manualForm.booking_location ||
                        String(device.location || "") ===
                          String(manualForm.booking_location || ""),
                    )
                    .map((device) => (
                      <option key={device.id} value={device.id}>
                        {device.name}
                        {device.location ? ` · ${device.location}` : ""}
                      </option>
                    ))}
                </select>
              </div>
            ) : null}
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Service
              </Label>
              <div className="mt-2 max-h-36 overflow-y-auto rounded-sm border border-[#D1D5DB] bg-white p-2 space-y-1">
                {services.length === 0 && (
                  <div className="text-sm text-[#6B7280] px-2 py-1">
                    No services added
                  </div>
                )}
                {services.map((service) => {
                  const checked = manualForm.service_ids.includes(service.id);
                  return (
                    <label
                      key={service.id}
                      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-sm border cursor-pointer ${checked ? "border-[#111827] bg-[#F9FAFB]" : "border-transparent hover:bg-[#F9FAFB]"}`}
                    >
                      <span className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [
                                  ...new Set([
                                    ...manualForm.service_ids,
                                    service.id,
                                  ]),
                                ]
                              : manualForm.service_ids.filter(
                                  (sid) => sid !== service.id,
                                );
                            setManualForm({
                              ...manualForm,
                              service_ids: next,
                              service_id: next[0] || "",
                            });
                          }}
                        />
                        <span>{service.name}</span>
                      </span>
                      <span className="text-sm font-semibold">
                        ₹{Number(service.price || 0).toLocaleString()}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Notes
              </Label>
              <Textarea
                value={manualForm.notes}
                onChange={(e) =>
                  setManualForm({ ...manualForm, notes: e.target.value })
                }
                rows={3}
                className="rounded-sm"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setManualOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
              >
                Add booking
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SalonStaffPanel() {
  const emptyForm = { name: "", mobile: "", password: "" };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [resetRow, setResetRow] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client/salon/staff");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  const createStaff = async () => {
    if (!form.name.trim() || !/^\d{10}$/.test(form.mobile) || form.password.length < 6) return;
    setSaving(true);
    try {
      await api.post("/client/salon/staff", { ...form, name: form.name.trim(), is_active: true });
      toast.success("Staff login created");
      setOpen(false); setForm(emptyForm); await loadStaff();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const setActive = async (row, active) => {
    try {
      await api.put(`/client/salon/staff/${encodeURIComponent(row.id)}`, { is_active: active });
      setRows((current) => current.map((item) => item.id === row.id ? { ...item, status: active ? "active" : "inactive" } : item));
      toast.success(active ? "Staff login activated" : "Staff login disabled");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const resetPassword = async () => {
    if (!resetRow || newPassword.length < 6) return;
    setSaving(true);
    try {
      await api.put(`/client/salon/staff/${encodeURIComponent(resetRow.id)}`, { password: newPassword });
      toast.success("Temporary password updated"); setResetRow(null); setNewPassword("");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const masked = (row) => {
    const digits = String(row.mobile || row.phone || "").replace(/\D/g, "");
    return digits.length >= 4 ? `••••••${digits.slice(-4)}` : "Mobile hidden";
  };

  return <>
    <section className="border border-[#D1D5DB] bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b bg-[#F9FAFB] px-3 py-2">
        <div><div className="text-sm font-bold">Salon staff access</div><div className="text-[11px] text-[#6B7280]">Staff can access only the current and pending queue</div></div>
        <Button onClick={() => setOpen(true)} className="h-8 rounded-sm bg-[#111827] px-3 text-xs text-white"><UserPlus className="mr-1.5 h-3.5 w-3.5" />Add staff</Button>
      </div>
      <div className="grid grid-cols-3 border-b bg-white text-center sm:max-w-md sm:text-left">
        <div className="border-r px-3 py-2"><div className="text-[9px] font-semibold uppercase tracking-wider text-[#6B7280]">Total</div><div className="text-lg font-bold">{rows.length}</div></div>
        <div className="border-r px-3 py-2"><div className="text-[9px] font-semibold uppercase tracking-wider text-[#6B7280]">Active</div><div className="text-lg font-bold text-emerald-700">{rows.filter((r) => r.status === "active").length}</div></div>
        <div className="px-3 py-2"><div className="text-[9px] font-semibold uppercase tracking-wider text-[#6B7280]">Permission</div><div className="pt-1 text-xs font-semibold">Queue only</div></div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] text-xs"><thead className="bg-[#F3F4F6] text-left text-[10px] uppercase tracking-wider text-[#6B7280]"><tr><th className="px-3 py-2">Staff</th><th className="px-3 py-2">Login mobile</th><th className="px-3 py-2">Access</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Actions</th></tr></thead><tbody>
          {rows.map((row) => <tr key={row.id} className="border-t"><td className="px-3 py-2 font-semibold">{row.name}</td><td className="px-3 py-2 font-mono">{masked(row)}</td><td className="px-3 py-2">Current & pending queue</td><td className="px-3 py-2"><span className={`font-semibold ${row.status === "active" ? "text-emerald-700" : "text-[#9CA3AF]"}`}>{row.status === "active" ? "Active" : "Disabled"}</span></td><td className="px-3 py-1.5 text-right"><Button variant="outline" className="mr-2 h-7 rounded-sm px-2 text-[10px]" onClick={() => { setResetRow(row); setNewPassword(""); }}>Reset password</Button><Switch checked={row.status === "active"} onCheckedChange={(value) => setActive(row, value)} /></td></tr>)}
          {!loading && !rows.length ? <tr><td colSpan="5" className="px-3 py-10 text-center text-[#6B7280]">No staff accounts. Create the first queue-only login.</td></tr> : null}
          {loading ? <tr><td colSpan="5" className="px-3 py-10 text-center text-[#6B7280]">Loading staff…</td></tr> : null}
        </tbody></table>
      </div>
    </section>

    <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-md rounded-sm"><DialogHeader><DialogTitle>Add Salon staff</DialogTitle></DialogHeader><div className="space-y-3"><div className="border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">This account receives queue-only permission. Accounting, inventory, customers and settings remain blocked.</div><div><Label>Staff name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div><div><Label>10-digit login mobile</Label><Input value={form.mobile} maxLength={10} inputMode="numeric" onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} /></div><div><Label>Temporary password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /><div className="mt-1 text-[10px] text-[#6B7280]">Minimum 6 characters</div></div></div><DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button disabled={saving || !form.name.trim() || form.mobile.length !== 10 || form.password.length < 6} onClick={createStaff}>{saving ? "Creating…" : "Create staff"}</Button></DialogFooter></DialogContent></Dialog>

    <Dialog open={!!resetRow} onOpenChange={(value) => { if (!value) setResetRow(null); }}><DialogContent className="max-w-sm rounded-sm"><DialogHeader><DialogTitle>Reset staff password</DialogTitle></DialogHeader><div><div className="mb-3 text-xs text-[#6B7280]">Set a new temporary password for <span className="font-semibold text-[#111827]">{resetRow?.name}</span>.</div><Label>New temporary password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /></div><DialogFooter><Button variant="outline" onClick={() => setResetRow(null)}>Cancel</Button><Button disabled={saving || newPassword.length < 6} onClick={resetPassword}>Update password</Button></DialogFooter></DialogContent></Dialog>
  </>;
}

function SalonInventoryPanel() {
  const emptyItem = {
    name: "",
    kind: "CONSUMABLE",
    unit: "ml",
    current_qty: 0,
    reorder_level: 0,
    cost_per_unit: 0,
    selling_price: 0,
    hsn_sac: "",
    gst_rate: 0,
    price_includes_tax: false,
    is_active: true,
  };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  const [form, setForm] = useState(emptyItem);
  const [editingId, setEditingId] = useState("");
  const [movement, setMovement] = useState({
    item_id: "",
    movement_type: "PURCHASE",
    quantity: 0,
    unit_cost: 0,
    note: "",
  });
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/client/salon/inventory/items");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const create = async (e) => {
    e.preventDefault();
    try {
      editingId
        ? await api.put(`/client/salon/inventory/items/${editingId}`, form)
        : await api.post("/client/salon/inventory/items", form);
      toast.success(
        editingId ? "Inventory item updated" : "Inventory item added",
      );
      setOpen(false);
      setEditingId("");
      setForm(emptyItem);
      load();
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail));
    }
  };
  const saveMovement = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/salon/inventory/movements", {
        ...movement,
        quantity: Number(movement.quantity || 0),
        unit_cost: Number(movement.unit_cost || 0),
      });
      toast.success("Stock updated");
      setMovementOpen(false);
      load();
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail));
    }
  };
  const low = items.filter((item) => item.is_low).length;
  const stockValue = items.reduce(
    (sum, item) =>
      sum + Number(item.current_qty || 0) * Number(item.cost_per_unit || 0),
    0,
  );
  return (
    <>
      <div className="border border-[#D1D5DB] bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-[#F9FAFB] px-3 py-2">
          <div>
            <div className="text-sm font-bold">Inventory register</div>
            <div className="text-[11px] text-[#6B7280]">
              Consumables, disposables and retail products
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-sm"
              onClick={() => {
                setMovement({
                  item_id: items[0]?.id || "",
                  movement_type: "PURCHASE",
                  quantity: 0,
                  unit_cost: 0,
                  note: "",
                });
                setMovementOpen(true);
              }}
              disabled={!items.length}
            >
              Stock entry
            </Button>
            <Button
              size="sm"
              className="h-8 rounded-sm"
              onClick={() => {
                setEditingId("");
                setForm(emptyItem);
                setOpen(true);
              }}
            >
              New item
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-3 border-b text-xs">
          <div className="p-2">
            <span className="text-[#6B7280]">Items</span>
            <strong className="ml-2">{items.length}</strong>
          </div>
          <div className="border-l p-2">
            <span className="text-[#6B7280]">Low stock</span>
            <strong className={`ml-2 ${low ? "text-red-600" : ""}`}>
              {low}
            </strong>
          </div>
          <div className="border-l p-2">
            <span className="text-[#6B7280]">Stock value</span>
            <strong className="ml-2">
              ₹
              {stockValue.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </strong>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F3F4F6]">
                <TableHead>Item</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>HSN/SAC</TableHead>
                <TableHead className="text-right">GST</TableHead>
                <TableHead className="text-right">On hand</TableHead>
                <TableHead className="text-right">Reorder</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Retail rate</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  key={item.id}
                  className={item.is_low ? "bg-red-50" : ""}
                >
                  <TableCell className="py-2 font-semibold">
                    {item.name}
                  </TableCell>
                  <TableCell className="py-2 text-xs">{item.kind}</TableCell>
                  <TableCell className="py-2 font-mono text-xs">
                    {item.hsn_sac || "—"}
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono">
                    {Number(item.gst_rate || 0)}%
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono">
                    {item.current_qty} {item.unit}
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono">
                    {item.reorder_level}
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono">
                    ₹{Number(item.cost_per_unit || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono">
                    ₹{Number(item.selling_price || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2"
                      onClick={() => {
                        setEditingId(item.id);
                        setForm({ ...emptyItem, ...item });
                        setOpen(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="py-12 text-center text-sm text-[#6B7280]"
                  >
                    No stock items. Add consumables, disposables or retail
                    products.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit inventory item" : "New inventory item"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Item name</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Type</Label>
              <select
                className="mt-1 h-10 w-full border px-2 text-sm"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
              >
                <option value="CONSUMABLE">Consumable</option>
                <option value="DISPOSABLE">Disposable</option>
                <option value="RETAIL">Retail product</option>
              </select>
            </div>
            <div>
              <Label>Unit</Label>
              <select
                className="mt-1 h-10 w-full border px-2 text-sm"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
              >
                {[
                  "g",
                  "kg",
                  "ml",
                  "litre",
                  "piece",
                  "packet",
                  "bottle",
                  "tube",
                ].map((unit) => (
                  <option key={unit}>{unit}</option>
                ))}
              </select>
            </div>
            {[
              [
                editingId
                  ? "Current quantity (use Stock Entry to change)"
                  : "Opening quantity",
                "current_qty",
              ],
              ["Reorder level", "reorder_level"],
              ["Cost per unit", "cost_per_unit"],
              ["Retail selling rate", "selling_price"],
            ].map(([label2, key]) => (
              <div key={key}>
                <Label>{label2}</Label>
                <Input
                  disabled={editingId && key === "current_qty"}
                  type="number"
                  min="0"
                  step="any"
                  value={form[key]}
                  onChange={(e) =>
                    setForm({ ...form, [key]: Number(e.target.value || 0) })
                  }
                />
              </div>
            ))}
            <div>
              <Label>HSN / SAC</Label>
              <Input
                inputMode="numeric"
                maxLength={12}
                value={form.hsn_sac}
                onChange={(e) =>
                  setForm({
                    ...form,
                    hsn_sac: e.target.value.replace(/\D/g, ""),
                  })
                }
              />
            </div>
            <div>
              <Label>GST rate</Label>
              <select
                className="mt-1 h-10 w-full border px-2 text-sm"
                value={form.gst_rate}
                onChange={(e) =>
                  setForm({ ...form, gst_rate: Number(e.target.value) })
                }
              >
                {[0, 5, 12, 18, 28].map((rate) => (
                  <option key={rate} value={rate}>
                    {rate}%
                  </option>
                ))}
              </select>
            </div>
            <label className="col-span-2 flex items-center gap-2 border px-3 py-2 text-xs">
              <Checkbox
                checked={Boolean(form.price_includes_tax)}
                onCheckedChange={(checked) =>
                  setForm({ ...form, price_includes_tax: Boolean(checked) })
                }
              />
              Retail selling rate includes GST
            </label>
            <DialogFooter className="col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Save item</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={movementOpen} onOpenChange={setMovementOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle>Stock entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveMovement} className="space-y-3">
            <div>
              <Label>Item</Label>
              <select
                required
                className="mt-1 h-10 w-full border px-2 text-sm"
                value={movement.item_id}
                onChange={(e) =>
                  setMovement({ ...movement, item_id: e.target.value })
                }
              >
                {items.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name} · {item.current_qty} {item.unit}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Movement</Label>
                <select
                  className="mt-1 h-10 w-full border px-2 text-sm"
                  value={movement.movement_type}
                  onChange={(e) =>
                    setMovement({ ...movement, movement_type: e.target.value })
                  }
                >
                  {[
                    "PURCHASE",
                    "WASTAGE",
                    "ADJUSTMENT_ADD",
                    "ADJUSTMENT_REMOVE",
                  ].map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  required
                  type="number"
                  min="0.001"
                  step="any"
                  value={movement.quantity}
                  onChange={(e) =>
                    setMovement({ ...movement, quantity: e.target.value })
                  }
                />
              </div>
            </div>
            <div>
              <Label>Unit cost</Label>
              <Input
                type="number"
                min="0"
                step="any"
                value={movement.unit_cost}
                onChange={(e) =>
                  setMovement({ ...movement, unit_cost: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Supplier / reason</Label>
              <Input
                value={movement.note}
                onChange={(e) =>
                  setMovement({ ...movement, note: e.target.value })
                }
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMovementOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Post stock</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Kept temporarily only for backward data visibility while the operational
// register is migrated into the double-entry accounting engine.
function LegacySalonAccountingPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const [summary, setSummary] = useState({
    service_sales: 0,
    other_income: 0,
    expenses: 0,
    net: 0,
    completed_services: 0,
  });
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    entry_type: "PURCHASE",
    entry_date: today,
    voucher_number: "",
    party_name: "",
    category: "Salon consumables",
    amount: "",
    payment_method: "CASH",
    note: "",
  });
  const entryCopy = {
    PURCHASE: {
      title: "Record a purchase bill",
      party: "Purchased from (supplier)",
      reference: "Supplier bill number",
      category: "What type of purchase?",
      note: "What did you purchase?",
      amount: "Total bill amount",
    },
    EXPENSE: {
      title: "Record a business expense",
      party: "Paid to",
      reference: "Bill / receipt number",
      category: "Expense head",
      note: "What was the expense for?",
      amount: "Amount paid",
    },
    INCOME: {
      title: "Record other income",
      party: "Received from",
      reference: "Reference number",
      category: "Income head",
      note: "What was the income for?",
      amount: "Amount received",
    },
    RECEIPT: {
      title: "Record money received",
      party: "Received from",
      reference: "Receipt number",
      category: "Received against",
      note: "Receipt details",
      amount: "Amount received",
    },
    PAYMENT: {
      title: "Record money paid",
      party: "Paid to",
      reference: "Payment reference",
      category: "Paid against",
      note: "Payment details",
      amount: "Amount paid",
    },
  };
  const copy = entryCopy[form.entry_type] || entryCopy.EXPENSE;
  const load = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([
        api.get("/client/salon/accounting/summary"),
        api.get("/client/salon/accounting/entries"),
      ]);
      setSummary(s.data || {});
      setEntries(Array.isArray(e.data) ? e.data : []);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/salon/accounting/entries", {
        ...form,
        amount: Number(form.amount || 0),
      });
      toast.success("Accounting entry posted");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };
  return (
    <>
      <div className="border border-[#D1D5DB] bg-white">
        <div className="flex items-center justify-between border-b bg-[#F9FAFB] px-3 py-2">
          <div>
            <div className="text-sm font-bold">Accounts workspace</div>
            <div className="text-[11px] text-[#6B7280]">
              Current month operational register
            </div>
          </div>
          <Button
            size="sm"
            className="h-8 rounded-sm"
            onClick={() => setOpen(true)}
          >
            New entry
          </Button>
        </div>
        <div className="grid grid-cols-2 border-b md:grid-cols-5">
          {[
            ["Service sales", summary.service_sales],
            ["Other income", summary.other_income],
            ["Expenses", summary.expenses],
            ["Net", summary.net],
            ["Services", summary.completed_services, false],
          ].map(([label2, value, money = true], index) => (
            <div key={label2} className={`${index ? "border-l" : ""} p-2`}>
              <div className="text-[10px] uppercase text-[#6B7280]">
                {label2}
              </div>
              <div
                className={`font-mono text-lg font-bold ${label2 === "Net" && Number(value) < 0 ? "text-red-600" : ""}`}
              >
                {money
                  ? `₹${Number(value || 0).toLocaleString()}`
                  : Number(value || 0)}
              </div>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F3F4F6]">
                <TableHead>Date</TableHead>
                <TableHead>Voucher</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Party / Category</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="py-2 font-mono text-xs">
                    {row.entry_date}
                  </TableCell>
                  <TableCell className="py-2 font-mono text-xs">
                    {row.voucher_number || "—"}
                  </TableCell>
                  <TableCell className="py-2 text-xs font-semibold">
                    {row.entry_type}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="text-xs font-semibold">
                      {row.party_name || row.category || "—"}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">{row.note}</div>
                  </TableCell>
                  <TableCell className="py-2 text-xs">
                    {row.payment_method}
                  </TableCell>
                  <TableCell className="py-2 text-right font-mono">
                    ₹{Number(row.amount || 0).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
              {!entries.length && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-12 text-center text-sm text-[#6B7280]"
                  >
                    No manual entries this month. Completed services are counted
                    automatically.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl rounded-sm">
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <div className="col-span-2 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
              Describe the actual business transaction. Ledger debit/credit
              posting will be generated by the complete accounting engine.
            </div>
            <div>
              <Label>Transaction</Label>
              <select
                className="mt-1 h-10 w-full border px-2 text-sm"
                value={form.entry_type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    entry_type: e.target.value,
                    category:
                      e.target.value === "PURCHASE" ? "Salon consumables" : "",
                    note: "",
                  })
                }
              >
                <option value="PURCHASE">I purchased something</option>
                <option value="EXPENSE">I paid a business expense</option>
                <option value="INCOME">I earned other income</option>
                <option value="RECEIPT">I received money</option>
                <option value="PAYMENT">I paid someone</option>
              </select>
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                required
                value={form.entry_date}
                onChange={(e) =>
                  setForm({ ...form, entry_date: e.target.value })
                }
              />
            </div>
            <div>
              <Label>{copy.party}</Label>
              <Input
                required
                value={form.party_name}
                placeholder={
                  form.entry_type === "PURCHASE"
                    ? "e.g. L'Oréal distributor"
                    : "Name"
                }
                onChange={(e) =>
                  setForm({ ...form, party_name: e.target.value })
                }
              />
            </div>
            <div>
              <Label>{copy.reference}</Label>
              <Input
                value={form.voucher_number}
                placeholder="As printed on the bill"
                onChange={(e) =>
                  setForm({ ...form, voucher_number: e.target.value })
                }
              />
            </div>
            <div className="col-span-2">
              <Label>{copy.note}</Label>
              <Input
                required
                value={form.note}
                placeholder={
                  form.entry_type === "PURCHASE"
                    ? "e.g. Hair colour tubes, developer and gloves"
                    : "Describe the transaction"
                }
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>
            <div>
              <Label>{copy.category}</Label>
              {form.entry_type === "PURCHASE" ? (
                <select
                  className="mt-1 h-10 w-full border px-2 text-sm"
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                >
                  <option>Salon consumables</option>
                  <option>Retail products for resale</option>
                  <option>Disposable supplies</option>
                  <option>Equipment / fixed asset</option>
                  <option>Office or general purchase</option>
                </select>
              ) : (
                <Input
                  required
                  value={form.category}
                  placeholder="Select or type account head"
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                />
              )}
            </div>
            <div>
              <Label>{copy.amount}</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                required
                value={form.amount}
                placeholder="0.00"
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div>
              <Label>Paid / received through</Label>
              <select
                className="mt-1 h-10 w-full border px-2 text-sm"
                value={form.payment_method}
                onChange={(e) =>
                  setForm({ ...form, payment_method: e.target.value })
                }
              >
                {["CASH", "UPI", "CARD", "BANK", "OTHER"].map((mode) => (
                  <option key={mode}>{mode}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end text-[11px] leading-4 text-[#6B7280]">
              For a stock purchase, also record quantities in Inventory so
              physical stock remains correct.
            </div>
            <DialogFooter className="col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Save transaction</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SalonAccountingPanel() {
  const voucherTypes = [
    ["Purchase", "Supplier bills, input tax and payables", "purchase"],
    ["Sales", "Service and product tax invoices", "sales"],
    ["Receipt", "Cash, bank, UPI and customer receipts", "receipt"],
    ["Payment", "Supplier and expense payments", "payment"],
    ["Contra", "Cash-to-bank and bank-to-bank transfer", "contra"],
    ["Journal", "Adjustments, provisions and closing entries", "journal"],
    ["Credit Note", "Sales returns and customer adjustments", "credit-note"],
    ["Debit Note", "Purchase returns and supplier adjustments", "debit-note"],
  ];
  const reports = [
    "Trial Balance",
    "General Ledger",
    "Day Book",
    "Cash Book",
    "Bank Book",
    "Income & Expenditure",
    "Balance Sheet",
    "Audit Trail",
  ];
  const openAccounting = (path = "dashboard") => {
    window.location.assign(`/client/salon/accounting/${path}`);
  };
  return (
    <div className="border border-[#D1D5DB] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-[#F9FAFB] px-3 py-2">
        <div>
          <div className="text-sm font-bold">
            Accounting & statutory records
          </div>
          <div className="text-[11px] text-[#6B7280]">
            Double-entry books, voucher registers, bank reconciliation and audit
            trail
          </div>
        </div>
        <Button
          size="sm"
          className="h-8 rounded-sm"
          onClick={() => openAccounting()}
        >
          Open accounting workspace
        </Button>
      </div>
      <div className="grid gap-0 md:grid-cols-[1.35fr_1fr]">
        <section className="border-b md:border-b-0 md:border-r">
          <div className="border-b bg-[#F3F4F6] px-3 py-2 text-[11px] font-bold uppercase tracking-wide">
            Voucher entry
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {voucherTypes.map(([name, description, path], index) => (
              <button
                key={name}
                type="button"
                onClick={() => openAccounting(path)}
                className={`min-h-[72px] border-b p-2 text-left hover:bg-[#F9FAFB] ${index % 2 ? "border-l" : ""} lg:border-l lg:first:border-l-0`}
              >
                <div className="text-xs font-bold text-[#111827]">{name}</div>
                <div className="mt-1 text-[10px] leading-4 text-[#6B7280]">
                  {description}
                </div>
              </button>
            ))}
          </div>
        </section>
        <section>
          <div className="border-b bg-[#F3F4F6] px-3 py-2 text-[11px] font-bold uppercase tracking-wide">
            Books & reports
          </div>
          <div className="grid grid-cols-2">
            {reports.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() =>
                  openAccounting(
                    {
                      "Trial Balance": "trial-balance",
                      "General Ledger": "general-ledger",
                      "Day Book": "day-book",
                      "Cash Book": "cash-book",
                      "Bank Book": "bank-book",
                      "Income & Expenditure": "profit-loss",
                      "Balance Sheet": "balance-sheet",
                      "Audit Trail": "audit-trail",
                    }[name],
                  )
                }
                className="border-b border-r px-3 py-2 text-left text-xs font-semibold hover:bg-[#F9FAFB]"
              >
                {name}
              </button>
            ))}
          </div>
          <div className="px-3 py-2 text-[10px] leading-4 text-[#6B7280]">
            GST returns and reconciliation will be shown here only after GST
            registration, tax ledgers and invoice series are configured.
          </div>
        </section>
      </div>
    </div>
  );
}

function isOrderToday(createdAt) {
  if (!createdAt) return false;
  const d = new Date(createdAt);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function orderMatchesSearch(order, query) {
  if (!query) return true;
  const collectText = (value) => {
    if (value == null) return [];
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    )
      return [String(value)];
    if (Array.isArray(value)) return value.flatMap(collectText);
    if (typeof value === "object")
      return Object.values(value).flatMap(collectText);
    return [];
  };
  const itemText = (order.items || [])
    .map((item) =>
      [item.name, item.sku, item.item_status, item.qty]
        .filter(Boolean)
        .join(" "),
    )
    .join(" ");
  const hay = [
    ...collectText(order),
    order.order_no,
    order.status,
    order.table_number,
    formatOrderDelivery(order),
    order.shopper_name,
    order.shopper_email,
    order.shopper_phone,
    order.shipping_address?.name,
    order.shipping_address?.phone,
    orderDeliverySearchText(order),
    itemText,
    Number(order.total || 0).toString(),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const compactHay = hay.replace(/\s+/g, "");
  const compactQuery = query.replace(/\s+/g, "");
  return hay.includes(query) || compactHay.includes(compactQuery);
}

function orderCustomerKey(order) {
  return (
    order.shopper_id ||
    order.shopper_phone ||
    order.shopper_email ||
    order.shopper_name ||
    order.id
  );
}

function orderBillKey(order) {
  if (orderFulfillmentType(order) === "offline") {
    const tableKey = String(order.table_number || "in-store")
      .trim()
      .toLowerCase();
    const customerKey = String(orderCustomerKey(order) || order.id)
      .trim()
      .toLowerCase();
    return `table:${tableKey}:customer:${customerKey}`;
  }
  return `customer:${orderCustomerKey(order)}`;
}

function orderBillLabel(order) {
  if (order?.bill_label) return order.bill_label;
  if (orderFulfillmentType(order) === "offline") {
    return formatOrderDelivery(order);
  }
  return (
    order.shopper_name || order.shopper_phone || order.shopper_email || "Guest"
  );
}

function orderCustomerLabel(order) {
  return (
    order?.customer_label ||
    order?.shopper_name ||
    order?.shipping_address?.name ||
    order?.shopper_phone ||
    order?.shopper_email ||
    "Guest"
  );
}

function orderItemCount(order) {
  return Number(
    order?.item_count ??
      (order?.items || []).reduce(
        (sum, item) => sum + Number(item.qty || 0),
        0,
      ),
  );
}

function orderPendingItemCount(order) {
  return Number(
    order?.pending_item_count ??
      (order?.items || []).filter(
        (item) => (item.item_status || "new") === "new",
      ).length,
  );
}

function formatShortTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function normalizeStockInput(value) {
  const text = String(value ?? "").trim();
  if (text === "-") return -1;
  if (!text) return 0;
  const parsed = Number.parseInt(text, 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(-1, parsed);
}

function displayStock(value) {
  return Number(value) === -1 ? "-" : Number(value || 0);
}

function cleanPhoneForWhatsApp(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function orderCustomerPhone(order) {
  return (
    order?.shopper_phone ||
    order?.shipping_phone ||
    order?.shipping_address?.phone ||
    ""
  );
}

function invoiceTotals(order, profile = {}) {
  const gstEnabled = profile.gst_enabled === true;
  const gstPercent = Math.max(0, Number(profile.gst_percent || 0));
  const total = Number(order?.total || 0);
  const taxable =
    gstEnabled && gstPercent > 0 ? total / (1 + gstPercent / 100) : total;
  const gstAmount = gstEnabled && gstPercent > 0 ? total - taxable : 0;
  return { gstEnabled, gstPercent, total, taxable, gstAmount };
}

function invoiceText(order, businessName, profile = {}) {
  const totals = invoiceTotals(order, profile);
  const lines = (order?.items || []).map((item) => {
    const qty = Number(item.qty || 0);
    const amount = Number(item.line_total || 0);
    return `${item.name || "Item"} x ${qty} - ₹ ${amount.toLocaleString()}`;
  });
  return [
    `${businessName || "RP Signage"} Bill`,
    order?.order_no ? `Bill No: ${order.order_no}` : "",
    `Customer: ${orderBillLabel(order || {})}`,
    `Date: ${new Date().toLocaleString()}`,
    "",
    ...lines,
    "",
    totals.gstEnabled && totals.gstPercent > 0
      ? `Taxable: ₹ ${totals.taxable.toFixed(2)}`
      : "",
    totals.gstEnabled && totals.gstPercent > 0
      ? `GST ${totals.gstPercent}%: ₹ ${totals.gstAmount.toFixed(2)}`
      : "",
    `Total: ₹ ${totals.total.toLocaleString()}`,
    "",
    "Thank you.",
  ]
    .filter(Boolean)
    .join("\n");
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function invoiceHtml(order, businessName, profile = {}) {
  const totals = invoiceTotals(order, profile);
  const itemRows = (order?.items || [])
    .map((item) => {
      const qty = Number(item.qty || 0);
      const rate = Number(item.unit_price || 0);
      const amount = Number(item.line_total || 0);
      return `
      <tr>
        <td class="item">
          <div class="item-name">${escapeHtml(item.name || "Item")}</div>
          <div class="muted">${qty} x ₹ ${rate.toLocaleString()}</div>
        </td>
        <td class="qty">${qty}</td>
        <td class="amount">₹ ${amount.toLocaleString()}</td>
      </tr>
    `;
    })
    .join("");
  const rows = (order?.items || [])
    .map(
      (item) => `
    <tr>
      <td>
        <strong>${escapeHtml(item.name || "Item")}</strong>
        <div class="muted">Qty ${escapeHtml(item.qty || 0)}</div>
      </td>
      <td class="right">₹ ${Number(item.line_total || 0).toLocaleString()}</td>
    </tr>
  `,
    )
    .join("");
  return `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>Bill ${escapeHtml(order?.order_no || "")}</title>
      <style>
        @page{size:80mm auto;margin:0}
        *{box-sizing:border-box}
        body{width:72mm;margin:0 auto;padding:3mm 2mm;color:#111827;font-family:Arial,'Roboto Mono',monospace;font-size:11px;line-height:1.28}
        h1{font-size:17px;line-height:1.1;margin:0;text-align:center;font-weight:900}
        .center{text-align:center}.muted{color:#4B5563;font-size:10px}.divider{border-top:1px dashed #111827;margin:7px 0}
        .meta{display:flex;justify-content:space-between;gap:8px;margin-top:2px}
        table{width:100%;border-collapse:collapse;margin-top:4px}
        th{border-bottom:1px solid #111827;padding:3px 0;font-size:10px;text-align:left}
        td{border-bottom:1px dashed #D1D5DB;padding:5px 0;vertical-align:top}
        .item-name{font-weight:800;font-size:11px}.item{width:52%}.qty{width:13%;text-align:center}.amount{width:35%;text-align:right;font-weight:800}
        .line{display:flex;justify-content:space-between;gap:8px;margin-top:4px}
        .total{border-top:2px solid #111827;margin-top:7px;padding-top:6px;font-size:15px;font-weight:900}
        .thanks{margin-top:9px;text-align:center;font-size:10px}.cut{margin-top:10px;text-align:center;color:#6B7280;font-size:10px}
        @media print{body{width:72mm;margin:0;padding:3mm 2mm}.no-print{display:none}}
      </style>
    </head>
    <body>
      <h1>${escapeHtml(businessName || "Restaurant")}</h1>
      <div class="center muted">Tax Invoice / Bill</div>
      <div class="divider"></div>
      <div class="meta"><span>Bill</span><strong>${escapeHtml(order?.order_no || "-")}</strong></div>
      <div class="meta"><span>${escapeHtml(orderFulfillmentType(order || {}) === "offline" ? "Table" : "Customer")}</span><strong>${escapeHtml(orderBillLabel(order || {}))}</strong></div>
      <div class="meta"><span>Date</span><strong>${escapeHtml(new Date().toLocaleString())}</strong></div>
      <div class="divider"></div>
      <table>
        <thead><tr><th>Item</th><th class="qty">Qty</th><th class="amount">Amount</th></tr></thead>
        <tbody>${itemRows || rows}</tbody>
      </table>
      ${
        totals.gstEnabled && totals.gstPercent > 0
          ? `
        <div class="line"><span>Taxable amount</span><strong>₹ ${totals.taxable.toFixed(2)}</strong></div>
        <div class="line"><span>GST ${totals.gstPercent}%</span><strong>₹ ${totals.gstAmount.toFixed(2)}</strong></div>
      `
          : ""
      }
      <div class="line total"><span>Total</span><span>₹ ${totals.total.toLocaleString()}</span></div>
      <div class="thanks">Thank you. Please visit again.</div>
      <div class="cut">--------------------------------</div>
    </body>
  </html>`;
}

function minutesAgo(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const mins = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs}h ${rem}m ago` : `${hrs}h ago`;
}

function isOrderSelectable(order) {
  return order.status !== "completed" && order.status !== "cancelled";
}

function orderActivitySnapshot(order) {
  const itemCount = (order.items || []).reduce(
    (sum, item) => sum + Number(item.qty || 0),
    0,
  );
  return {
    itemCount,
    total: Number(order.total || 0),
  };
}

function groupActiveOrdersForDisplay(orders) {
  const grouped = new Map();
  const finalOrders = [];

  (orders || []).forEach((order) => {
    if (!isOrderSelectable(order)) {
      finalOrders.push(order);
      return;
    }
    const key = orderBillKey(order);
    if (!key) {
      finalOrders.push(order);
      return;
    }
    if (!grouped.has(key)) {
      grouped.set(key, {
        ...order,
        items: (order.items || []).map((item, index) => ({
          ...item,
          _order_id: order.id,
          _item_ref: item.line_id || String(index),
        })),
        total: Number(order.total || 0),
        subtotal: Number(order.subtotal || order.total || 0),
        _sourceOrderIds: [order.id],
        _sourceOrderNos: [order.order_no].filter(Boolean),
      });
      return;
    }
    const target = grouped.get(key);
    target.items = [
      ...(target.items || []),
      ...(order.items || []).map((item, index) => ({
        ...item,
        _order_id: order.id,
        _item_ref: item.line_id || String(index),
        _source_order_no: order.order_no,
      })),
    ];
    target.total = Number(target.total || 0) + Number(order.total || 0);
    target.subtotal =
      Number(target.subtotal || 0) + Number(order.subtotal || order.total || 0);
    target._sourceOrderIds = [...(target._sourceOrderIds || []), order.id];
    target._sourceOrderNos = [
      ...(target._sourceOrderNos || []),
      order.order_no,
    ].filter(Boolean);
    target.updated_at =
      [target.updated_at, order.updated_at].filter(Boolean).sort().pop() ||
      target.updated_at;
    if (new Date(order.created_at || 0) < new Date(target.created_at || 0))
      target.created_at = order.created_at;
  });

  return [...grouped.values(), ...finalOrders].sort((a, b) =>
    String(b.updated_at || b.created_at || "").localeCompare(
      String(a.updated_at || a.created_at || ""),
    ),
  );
}

function HotelRestaurantPanel({ withPageHeader = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const isBasicMenu = me?.restaurant_mode === "menu_only";
  const basicMenuLevel2 = isBasicMenu && me?.basic_menu_level2_enabled === true;
  const hotelTabs = useMemo(
    () =>
      new Set(isBasicMenu
        ? ["products", "categories", "banners", ...(basicMenuLevel2 ? ["basic-orders"] : []), "settings"]
        : ["hotel", "products", "categories", "banners", "orders", "settings"]),
    [isBasicMenu, basicMenuLevel2],
  );
  const requestedTab = searchParams.get("tab");
  const activeHotelTab = hotelTabs.has(requestedTab) ? requestedTab : (isBasicMenu ? "products" : "hotel");
  const changeHotelTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", nextTab);
    setSearchParams(next, { replace: true });
  };
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [restaurantTables, setRestaurantTables] = useState([]);
  const [diningSessions, setDiningSessions] = useState([]);
  const [activeDiningSession, setActiveDiningSession] = useState(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState("offline");
  const [orderStatusFilter, setOrderStatusFilter] = useState("not_completed");
  const [orderTodayOnly, setOrderTodayOnly] = useState(true);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [attentionOrderIds, setAttentionOrderIds] = useState([]);
  const [expandedAdvancedOrderId, setExpandedAdvancedOrderId] = useState("");
  const [showDiningParties, setShowDiningParties] = useState(false);
  const [diningPartySearch, setDiningPartySearch] = useState("");
  const orderActivityRef = useRef(null);
  const [completingSelected, setCompletingSelected] = useState(false);
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const empty = {
    name: "",
    price: 0,
    sku: "",
    description: "",
    image_url: "",
    image_urls: [],
    category_id: "",
    menu_display_order: 0,
    stock: -1,
    tags: "",
    is_published: true,
    compare_at_price: "",
  };
  const emptyCat = { name: "", sort_order: 0, is_active: true };
  const [form, setForm] = useState(empty);
  const [catForm, setCatForm] = useState(emptyCat);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profile, setProfile] = useState({
    headline: "",
    description: "",
    hours: "",
    is_open: true,
    image_url: "",
    announcement: "",
    banner_slides: [],
  });
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const emptyWaiter = {
    phone: "",
    name: "",
    email: "",
    password: "",
    table_number: "",
    assignments: [],
    product_id: "",
    qty: 1,
    notes: "",
  };
  const [waiterForm, setWaiterForm] = useState(emptyWaiter);
  const [waiterOrderId, setWaiterOrderId] = useState("");
  const [manualWaiterMode, setManualWaiterMode] = useState(true);
  const [waiterDishQuery, setWaiterDishQuery] = useState("");
  const [waiterDishSearchOpen, setWaiterDishSearchOpen] = useState(false);
  const [waiterBusy, setWaiterBusy] = useState(false);
  const [billOrder, setBillOrder] = useState(null);
  const [seoForm, setSeoForm] = useState({
    seo_title: "",
    meta_description: "",
    meta_keywords: "",
    canonical_url: "",
    og_image_url: "",
    business_address: "",
    google_map_url: "",
  });
  const [seoBusy, setSeoBusy] = useState(false);

  const bookingSlug = useMemo(() => {
    if (!me) return "";
    // Client names are not unique (for example, one owner can have both a
    // society and restaurant account). Prefer the unique client code so a
    // public store link can never resolve to another client with the same name.
    return (
      me.client_code || me.clientCode || me.public_booking_slug || me.id || ""
    );
  }, [me]);

  const storeUrl = useMemo(() => {
    if (
      me?.custom_booking_domain_status === "active" &&
      me?.custom_booking_domain
    ) {
      return `https://${me.custom_booking_domain}`;
    }
    const ref = bookingSlug || me?.public_booking_slug || me?.id || "";
    if (!ref) return "";
    return `${window.location.origin}/store/${encodeURIComponent(ref)}`;
  }, [
    bookingSlug,
    me?.custom_booking_domain,
    me?.custom_booking_domain_status,
    me?.id,
    me?.public_booking_slug,
  ]);

  const basicMenuUrl = useMemo(() => {
    if (!bookingSlug) return "";
    return `${window.location.origin}/menu/${encodeURIComponent(bookingSlug)}`;
  }, [bookingSlug]);

  const basicMenuQrUrl = useMemo(() => {
    if (!bookingSlug) return "";
    return `${window.location.origin}/api/public/restaurant/${encodeURIComponent(bookingSlug)}/menu-qr.png`;
  }, [bookingSlug]);

  const downloadBasicMenuQrCard = () => {
    if (!basicMenuQrUrl) return;
    const qrImage = new Image();
    qrImage.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1200;
      canvas.height = 1500;
      const ctx = canvas.getContext("2d");
      const restaurantName = me?.organization_name || me?.name || "Restaurant";
      ctx.fillStyle = "#F7F3EA";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#0F3D33";
      ctx.fillRect(0, 0, canvas.width, 260);
      ctx.textAlign = "center";
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "700 72px Arial";
      let titleSize = 72;
      while (ctx.measureText(restaurantName).width > 1040 && titleSize > 40) {
        titleSize -= 4;
        ctx.font = `700 ${titleSize}px Arial`;
      }
      ctx.fillText(restaurantName, 600, 125);
      ctx.fillStyle = "#FCD34D";
      ctx.font = "700 32px Arial";
      ctx.fillText("SCAN TO VIEW OUR MENU", 600, 195);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(150, 330, 900, 900);
      ctx.drawImage(qrImage, 210, 390, 780, 780);
      ctx.fillStyle = "#0F172A";
      ctx.font = "700 46px Arial";
      ctx.fillText("Menu & prices", 600, 1325);
      ctx.fillStyle = "#64748B";
      ctx.font = "28px Arial";
      ctx.fillText("No login, table code or ordering required", 600, 1380);
      ctx.font = "22px Arial";
      ctx.fillText(basicMenuUrl, 600, 1435);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `restaurant-${bookingSlug}-menu-card.png`;
        link.click();
        URL.revokeObjectURL(link.href);
      }, "image/png");
    };
    qrImage.onerror = () => toast.error("Could not prepare the menu QR card");
    qrImage.src = basicMenuQrUrl;
  };
  const printBasicMenuQr = () => {
    if (!basicMenuQrUrl) return;
    const popup = window.open("", "_blank", "width=720,height=820");
    if (!popup) {
      toast.error("Allow pop-ups to print the menu QR");
      return;
    }
    const restaurantName = me?.organization_name || me?.name || "Restaurant";
    popup.document.write(`<!doctype html><html><head><title>Menu QR</title><style>body{font-family:Arial,sans-serif;margin:0;display:grid;place-items:center;min-height:100vh;text-align:center}.card{padding:32px}.card img{width:360px;height:360px;object-fit:contain}.card h1{margin:0 0 8px;font-size:28px}.card p{margin:0 0 20px;color:#475569}@media print{.card{padding:0}}</style></head><body><div class="card"><h1>${restaurantName}</h1><p>Scan to view our menu and prices</p><img src="${basicMenuQrUrl}" alt="Menu QR" onload="setTimeout(function(){window.print()},250)" /></div></body></html>`);
    popup.document.close();
  };
  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/client/me");
      setMe(data);
      const stored = data.retailer_profile || {};
      setProfile({
        headline: stored.headline || "",
        description: stored.description || "",
        hours: stored.hours || "",
        is_open: stored.is_open !== false,
        image_url: stored.image_url || "",
        announcement: stored.announcement || "",
        basic_menu_background_color: stored.basic_menu_background_color || "#F7F3EA",
        basic_menu_banner_url: stored.basic_menu_banner_url || "",
        basic_menu_banner_videos: Array.isArray(stored.basic_menu_banner_videos) ? stored.basic_menu_banner_videos : [],
        basic_menu_ticker_text: stored.basic_menu_ticker_text || "",
        basic_menu_ticker_background_color: stored.basic_menu_ticker_background_color || "#0F3D33",
        basic_menu_ticker_text_color: stored.basic_menu_ticker_text_color || "#FFFFFF",
        basic_menu_ticker_font_size: Number(stored.basic_menu_ticker_font_size || 14),
        basic_menu_item_background_url: stored.basic_menu_item_background_url || "",
        basic_menu_upi_id: stored.basic_menu_upi_id || "",
        basic_menu_upi_payee_name: stored.basic_menu_upi_payee_name || data.organization_name || data.name || "",
        banner_slides: Array.isArray(stored.banner_slides)
          ? stored.banner_slides
          : [],
        share_store_visible: stored.share_store_visible === true,
        gst_enabled: stored.gst_enabled === true,
        gst_percent: Number(stored.gst_percent || 0),
      });
      try {
        const seo = await api.get("/client/seo-settings");
        setSeoForm((prev) => ({ ...prev, ...(seo.data || {}) }));
      } catch {}
    } catch {}
  }, []);

  const uploadProductImage = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "storefront");
      fd.append("zone", "main");
      fd.append("name", file.name);
      const { data } = await api.post("/client/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.id
        ? `${API_BASE}/media/serve/${data.id}`
        : resolveMediaUrl(data.public_url || data.url || "");
      setForm((prev) => ({
        ...prev,
        image_urls: [...(prev.image_urls || []), url].filter(Boolean),
        image_url: prev.image_url || url,
      }));
      toast.success("Menu item image uploaded");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setUploadingImage(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setItems((await api.get("/client/products")).data);
    } catch {}
  }, []);
  const loadCategories = useCallback(async () => {
    try {
      setCategories((await api.get("/client/categories")).data);
    } catch {}
  }, []);
  const loadRestaurantTables = useCallback(async () => {
    try {
      setRestaurantTables((await api.get("/hotel/tables")).data || []);
    } catch {}
  }, []);
  const loadDiningSessions = useCallback(async () => {
    try {
      setDiningSessions((await api.get("/hotel/dining-sessions")).data || []);
    } catch {}
  }, []);
  const loadOrders = useCallback(async () => {
    try {
      const nextOrders = (await api.get("/client/orders")).data;
      setOrders(nextOrders);
      setAttentionOrderIds((prev) => {
        const previousActivity = orderActivityRef.current;
        const nextActivity = new Map(
          nextOrders.map((order) => [order.id, orderActivitySnapshot(order)]),
        );
        orderActivityRef.current = nextActivity;

        const activeIds = new Set(
          nextOrders.filter(isOrderSelectable).map((order) => order.id),
        );
        const stillActive = prev.filter((id) => activeIds.has(id));
        if (!previousActivity) return stillActive;

        const newActivityIds = nextOrders
          .filter(isOrderSelectable)
          .filter((order) => {
            const previous = previousActivity.get(order.id);
            const current = nextActivity.get(order.id);
            if (!current) return false;
            if (!previous) return true;
            return (
              current.itemCount > previous.itemCount ||
              current.total > previous.total
            );
          })
          .map((order) => order.id);

        return Array.from(new Set([...stillActive, ...newActivityIds]));
      });
    } catch {}
  }, []);

  useEffect(() => {
    load();
    loadCategories();
    loadOrders();
    loadProfile();
    loadRestaurantTables();
    loadDiningSessions();
  }, [
    load,
    loadCategories,
    loadOrders,
    loadProfile,
    loadRestaurantTables,
    loadDiningSessions,
  ]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadOrders();
      loadDiningSessions();
      loadRestaurantTables();
    }, 15000);
    return () => clearInterval(timer);
  }, [loadOrders, loadDiningSessions, loadRestaurantTables]);

  useEffect(() => {
    if (!attentionOrderIds.length) return undefined;
    const timer = setTimeout(() => setAttentionOrderIds([]), 90000);
    return () => clearTimeout(timer);
  }, [attentionOrderIds]);

  useEffect(() => {
    setSelectedOrderIds((prev) => {
      const selectable = new Set(
        orders.filter(isOrderSelectable).map((order) => order.id),
      );
      return prev.filter((id) => selectable.has(id));
    });
  }, [orders]);

  const saveProfile = async () => {
    try {
      const payload = {
        headline: profile.headline || "",
        description: profile.description || "",
        hours: profile.hours || "",
        is_open: profile.is_open !== false,
        image_url: profile.image_url || "",
        announcement: profile.announcement || "",
        basic_menu_background_color: profile.basic_menu_background_color || "#F7F3EA",
        basic_menu_banner_url: profile.basic_menu_banner_url || "",
        basic_menu_banner_videos: profile.basic_menu_banner_videos || [],
        basic_menu_ticker_text: profile.basic_menu_ticker_text || "",
        basic_menu_ticker_background_color: profile.basic_menu_ticker_background_color || "#0F3D33",
        basic_menu_ticker_text_color: profile.basic_menu_ticker_text_color || "#FFFFFF",
        basic_menu_ticker_font_size: Number(profile.basic_menu_ticker_font_size || 14),
        basic_menu_item_background_url: profile.basic_menu_item_background_url || "",
        basic_menu_upi_id: (profile.basic_menu_upi_id || "").trim(),
        basic_menu_upi_payee_name: (profile.basic_menu_upi_payee_name || "").trim(),
        share_store_visible: profile.share_store_visible === true,
        gst_enabled: profile.gst_enabled === true,
        gst_percent: Number(profile.gst_percent || 0),
        banner_slides: (profile.banner_slides || []).map((slide, idx) => ({
          id: slide.id || String(idx),
          image_url: slide.image_url || "",
          title: slide.title || "",
          subtitle: slide.subtitle || "",
          link_url: slide.link_url || "",
          sort_order: idx,
          is_active: slide.is_active !== false,
        })),
      };
      await api.put("/client/retailer/profile", payload);
      toast.success("Hotel / Restaurant profile saved");
      loadProfile();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const saveSeoSettings = async () => {
    setSeoBusy(true);
    try {
      const { data } = await api.put("/client/seo-settings", seoForm);
      setSeoForm((prev) => ({ ...prev, ...(data || {}) }));
      toast.success("SEO settings saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSeoBusy(false);
    }
  };

  const copyStoreLink = async () => {
    if (!storeUrl) return;
    try {
      await navigator.clipboard.writeText(storeUrl);
      toast.success("Public ordering link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const uploadCoverImage = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "storefront");
      fd.append("zone", "main");
      fd.append("name", file.name);
      const { data } = await api.post("/client/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.id
        ? `${API_BASE}/media/serve/${data.id}`
        : resolveMediaUrl(data.public_url || data.url || "");
      setProfile((prev) => ({ ...prev, image_url: url }));
      toast.success("Cover image uploaded");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setUploadingCover(false);
    }
  };

  const uploadBasicMenuImage = async (file, field, label) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "restaurant-basic-menu");
      fd.append("zone", "main");
      fd.append("name", file.name);
      const { data } = await api.post("/client/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.id ? `${API_BASE}/media/serve/${data.id}` : resolveMediaUrl(data.public_url || data.url || "");
      setProfile((prev) => ({ ...prev, [field]: url }));
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setUploadingCover(false);
    }
  };

  const readBasicMenuVideoDuration = (file) => new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const duration = Number(video.duration || 0);
      URL.revokeObjectURL(objectUrl);
      resolve(duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read video duration"));
    };
    video.src = objectUrl;
  });

  const uploadBasicMenuBannerMedia = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      await uploadBasicMenuImage(file, "basic_menu_banner_url", "Basic menu banner");
      return;
    }
    if ((profile.basic_menu_banner_videos || []).length >= 2) {
      toast.error("You can add a maximum of 2 top-banner videos");
      return;
    }
    let durationSeconds = 0;
    try {
      durationSeconds = await readBasicMenuVideoDuration(file);
    } catch (error) {
      toast.error(error.message);
      return;
    }
    if (!durationSeconds || durationSeconds > 20.25) {
      toast.error("Top-banner video must be 20 seconds or shorter");
      return;
    }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "restaurant-basic-menu");
      fd.append("zone", "main");
      fd.append("name", file.name);
      const { data } = await api.post("/client/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.id ? `${API_BASE}/media/serve/${data.id}` : resolveMediaUrl(data.public_url || data.url || "");
      const item = { id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()), url, duration_seconds: durationSeconds };
      setProfile((prev) => ({ ...prev, basic_menu_banner_videos: [...(prev.basic_menu_banner_videos || []), item] }));
      toast.success("Top-banner video added — save settings to publish it");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setUploadingCover(false);
    }
  };

  const uploadBannerImage = async (file) => {
    if (!file) return;
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "storefront");
      fd.append("zone", "main");
      fd.append("name", file.name);
      const { data } = await api.post("/client/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data.id
        ? `${API_BASE}/media/serve/${data.id}`
        : resolveMediaUrl(data.public_url || data.url || "");
      const slide = {
        id:
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : String(Date.now()),
        image_url: url,
        title: "",
        subtitle: "",
        link_url: "",
        sort_order: (profile.banner_slides || []).length,
        is_active: true,
      };
      setProfile((prev) => ({
        ...prev,
        banner_slides: [...(prev.banner_slides || []), slide],
      }));
      toast.success("Banner slide added — add title and save storefront");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setUploadingBanner(false);
    }
  };

  const updateBannerSlide = (id, patch) => {
    setProfile((prev) => ({
      ...prev,
      banner_slides: (prev.banner_slides || []).map((slide) =>
        slide.id === id ? { ...slide, ...patch } : slide,
      ),
    }));
  };

  const removeBannerSlide = (id) => {
    setProfile((prev) => ({
      ...prev,
      banner_slides: (prev.banner_slides || []).filter(
        (slide) => slide.id !== id,
      ),
    }));
  };

  const moveBannerSlide = (id, direction) => {
    setProfile((prev) => {
      const slides = [...(prev.banner_slides || [])];
      const index = slides.findIndex((slide) => slide.id === id);
      if (index < 0) return prev;
      const target = index + direction;
      if (target < 0 || target >= slides.length) return prev;
      [slides[index], slides[target]] = [slides[target], slides[index]];
      return {
        ...prev,
        banner_slides: slides.map((slide, i) => ({ ...slide, sort_order: i })),
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (typeof payload.tags === "string")
        payload.tags = payload.tags
          .split(/[;,|]/)
          .map((t) => t.trim())
          .filter(Boolean);
      payload.image_urls = (payload.image_urls || []).filter(Boolean);
      payload.image_url = payload.image_urls[0] || payload.image_url || "";
      // Restaurant dishes have availability, never an inventory quantity.
      payload.stock = -1;
      if (!payload.category_id) payload.category_id = null;
      if (payload.compare_at_price === "" || payload.compare_at_price == null)
        delete payload.compare_at_price;
      else payload.compare_at_price = Number(payload.compare_at_price);
      if (editing) await api.put(`/client/products/${editing.id}`, payload);
      else await api.post("/client/products", payload);
      toast.success(editing ? "Menu item updated" : "Menu item added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCat)
        await api.put(`/client/categories/${editingCat.id}`, catForm);
      else await api.post("/client/categories", catForm);
      toast.success(editingCat ? "Category updated" : "Category added");
      setCatOpen(false);
      loadCategories();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this menu item?")) return;
    try {
      await api.delete(`/client/products/${id}`);
      toast.success("Deleted");
      load();
    } catch {}
  };

  const removeCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.delete(`/client/categories/${id}`);
      toast.success("Deleted");
      loadCategories();
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const updateOrderStatus = async (orderIdOrOrder, status) => {
    try {
      const ids = Array.isArray(orderIdOrOrder?._sourceOrderIds)
        ? orderIdOrOrder._sourceOrderIds
        : [orderIdOrOrder];
      await Promise.all(
        ids.map((orderId) =>
          api.put(`/client/orders/${orderId}/status`, { status }),
        ),
      );
      toast.success(`Order marked ${status}`);
      loadOrders();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const updateOrderItemStatus = async (orderId, itemRef, status) => {
    try {
      const response = await api.put(
        `/client/orders/${orderId}/items/${encodeURIComponent(itemRef)}/status`,
        { status },
      );
      if (response.data?.id) {
        setOrders((current) =>
          current.map((order) =>
            order.id === response.data.id ? response.data : order,
          ),
        );
      }
      setAttentionOrderIds((prev) => prev.filter((id) => id !== orderId));
      toast.success(`Item marked ${status}`);
      await loadOrders();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const completeSelectedOrders = async () => {
    if (!selectedOrderIds.length) return;
    setCompletingSelected(true);
    try {
      const ids = selectedOrders.flatMap(
        (order) => order._sourceOrderIds || [order.id],
      );
      await Promise.all(
        ids.map((orderId) =>
          api.put(`/client/orders/${orderId}/status`, { status: "completed" }),
        ),
      );
      toast.success(
        `${selectedOrderIds.length} order${selectedOrderIds.length === 1 ? "" : "s"} marked completed`,
      );
      setSelectedOrderIds([]);
      loadOrders();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
      loadOrders();
    } finally {
      setCompletingSelected(false);
    }
  };

  const generateDiningCode = async () => {
    const assignments = waiterForm.assignments || [];
    if (!assignments.length) {
      toast.error("Select every seat used by this customer or family.");
      return;
    }
    setWaiterBusy(true);
    try {
      const tableNumber = String(assignments[0].table_number);
      const seatLabel = assignments
        .map((seat) => `T${seat.table_number}-S${seat.seat_number}`)
        .join(", ");
      const { data } = await api.post("/hotel/dining-sessions", {
        table_number: tableNumber,
        seat_label: seatLabel,
        customer_name: waiterForm.name.trim(),
        customer_mobile: waiterForm.phone.trim(),
        assignments,
      });
      setActiveDiningSession(data);
      toast.success(`Customer code ${data.code} generated`);
      await Promise.all([loadDiningSessions(), loadRestaurantTables()]);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setWaiterBusy(false);
    }
  };

  const cancelDiningSession = async (sessionId) => {
    if (!window.confirm("Cancel this dining party and release all its seats?"))
      return;
    try {
      await api.delete(`/hotel/dining-sessions/${sessionId}`);
      if (activeDiningSession?.id === sessionId) {
        setActiveDiningSession(null);
        setWaiterOrderId("");
        setWaiterForm((prev) => ({
          ...emptyWaiter,
          phone: prev.phone,
          name: prev.name,
        }));
      }
      toast.success("Dining party cancelled and seats released");
      await Promise.all([loadDiningSessions(), loadRestaurantTables()]);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const createWaiterOrder = async (e) => {
    e.preventDefault();
    if (manualWaiterMode) {
      await generateDiningCode();
      return;
    }
    const effectivePhone = (
      waiterForm.phone ||
      selectedWaiterOrder?.shopper_phone ||
      ""
    ).trim();
    const effectiveName = (
      waiterForm.name ||
      selectedWaiterOrder?.shopper_name ||
      ""
    ).trim();
    const effectiveAssignments = waiterForm.assignments?.length
      ? waiterForm.assignments
      : selectedWaiterOrder?.dining_assignments || [];
    const effectiveTable = String(
      selectedWaiterOrder?.table_number ||
        effectiveAssignments[0]?.table_number ||
        waiterForm.table_number ||
        "",
    ).trim();
    const effectiveSeat = effectiveAssignments.length
      ? effectiveAssignments
          .map((seat) => `T${seat.table_number}-S${seat.seat_number}`)
          .join(", ")
      : String(selectedWaiterOrder?.seat_label || "").trim();
    if (
      !effectiveTable ||
      (!selectedWaiterOrder && !effectiveAssignments.length && !effectiveSeat) ||
      !waiterForm.product_id
    ) {
      toast.error(
        "Select at least one table seat and a dish. Customer mobile is optional.",
      );
      return;
    }
    setWaiterBusy(true);
    let createdDiningSessionId = "";
    try {
      let diningSessionId =
        selectedWaiterOrder?.dining_session_id || activeDiningSession?.id || "";
      if (
        !selectedWaiterOrder &&
        !diningSessionId &&
        effectiveAssignments.length
      ) {
        const session = await api.post("/hotel/dining-sessions", {
          table_number: effectiveTable,
          seat_label: effectiveSeat,
          customer_name: effectiveName,
          customer_mobile: effectivePhone,
          assignments: effectiveAssignments,
        });
        diningSessionId = session.data?.id || "";
        createdDiningSessionId = diningSessionId;
      }
      const orderResponse = await api.post("/hotel/orders", {
        phone: effectivePhone,
        name: effectiveName,
        email: waiterForm.email,
        password: waiterForm.password,
        table_number: effectiveTable,
        seat_label: effectiveSeat,
        dining_session_id: diningSessionId,
        notes: waiterForm.notes,
        items: [
          {
            product_id: waiterForm.product_id,
            qty: Number(waiterForm.qty || 1),
          },
        ],
      });
      toast.success("Dish added to active table bill");
      if (orderResponse.data?.id) setWaiterOrderId(orderResponse.data.id);
      setWaiterForm((prev) => ({
        ...emptyWaiter,
        phone: effectivePhone || prev.phone,
        name: effectiveName || prev.name,
        table_number: effectiveTable || prev.table_number,
        assignments: effectiveAssignments,
      }));
      setWaiterDishQuery("");
      setWaiterDishSearchOpen(false);
      loadOrders();
      loadRestaurantTables();
      loadDiningSessions();
    } catch (e) {
      if (createdDiningSessionId)
        await api
          .delete(`/hotel/dining-sessions/${createdDiningSessionId}`)
          .catch(() => {});
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setWaiterBusy(false);
    }
  };

  const printFinalBill = (order) => {
    const gstEnabled = profile.gst_enabled === true;
    const gstPercent = Math.max(0, Number(profile.gst_percent || 0));
    const total = Number(order.total || 0);
    const taxable =
      gstEnabled && gstPercent > 0 ? total / (1 + gstPercent / 100) : total;
    const gstAmount = gstEnabled && gstPercent > 0 ? total - taxable : 0;
    const rows = (order.items || [])
      .map(
        (item) => `
      <tr>
        <td>${item.name || ""} x ${item.qty || 0}</td>
        <td style="text-align:right">₹ ${Number(item.line_total || 0).toLocaleString()}</td>
      </tr>
    `,
      )
      .join("");
    const html = `
      <html><head><title>Bill ${order.order_no || ""}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:24px;color:#111827}
        h1{font-size:20px;margin:0 0 4px} .muted{color:#6B7280;font-size:12px}
        table{width:100%;border-collapse:collapse;margin-top:16px} td{border-bottom:1px solid #E5E7EB;padding:8px 0;font-size:13px}
        .total{font-size:18px;font-weight:800}.right{text-align:right}.line{display:flex;justify-content:space-between;margin-top:8px}
      </style></head><body>
      <h1>${me?.name || "Restaurant"}</h1>
      <div class="muted">Bill ${order.order_no || ""} · ${orderBillLabel(order)}</div>
      <div class="muted">${new Date().toLocaleString()}</div>
      <table>${rows}</table>
      ${
        gstEnabled && gstPercent > 0
          ? `
        <div class="line"><span>Taxable amount</span><strong>₹ ${taxable.toFixed(2)}</strong></div>
        <div class="line"><span>GST ${gstPercent}%</span><strong>₹ ${gstAmount.toFixed(2)}</strong></div>
      `
          : ""
      }
      <div class="line total"><span>Total</span><span>₹ ${total.toLocaleString()}</span></div>
      <div class="muted" style="margin-top:20px">Thank you.</div>
      <script>window.print();</script>
      </body></html>`;
    const win = window.open("", "_blank", "width=420,height=640");
    if (!win) {
      toast.error("Allow popups to print the bill");
      return;
    }
    win.document.write(html);
    win.document.close();
  };

  const printCurrentBill = () => {
    if (!billOrder) return;
    if (billOrder.status !== "completed") {
      toast.error("Complete billing before printing the final bill");
      return;
    }
    const html = invoiceHtml(billOrder, me?.name || "Restaurant", profile);
    const jobName = `RP Signage Bill ${billOrder.order_no || ""}`.trim();
    try {
      if (window.RpSignageAndroid?.printHtml) {
        window.RpSignageAndroid.printHtml(html, jobName);
        return;
      }
    } catch {}
    try {
      const frame = document.createElement("iframe");
      frame.title = jobName;
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      frame.style.opacity = "0";
      document.body.appendChild(frame);
      const doc = frame.contentWindow?.document;
      if (!doc) throw new Error("Print frame is not available");
      doc.open();
      doc.write(html);
      doc.close();
      frame.onload = () => {
        try {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
        } finally {
          setTimeout(() => frame.remove(), 1500);
        }
      };
    } catch {
      toast.error("Print is not available on this device");
    }
  };

  const shareBillToWhatsApp = (order) => {
    const phone = cleanPhoneForWhatsApp(orderCustomerPhone(order));
    const text = invoiceText(order, me?.name || "Restaurant", profile);
    if (!phone) {
      toast.error("Customer WhatsApp number is not available");
      return;
    }
    window.location.href = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  };

  const shareCurrentBill = async () => {
    if (!billOrder) return;
    const text = invoiceText(billOrder, me?.name || "Restaurant", profile);
    try {
      if (window.RpSignageAndroid?.shareText) {
        window.RpSignageAndroid.shareText(
          `Bill ${billOrder.order_no || ""}`.trim(),
          text,
        );
        return;
      }
      if (navigator.share) {
        await navigator.share({
          title: `Bill ${billOrder.order_no || ""}`,
          text,
        });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Bill copied");
      }
    } catch {}
  };

  const categoryName = (id) => categories.find((c) => c.id === id)?.name || "-";
  const sortedMenuItems = useMemo(() => {
    const categoryOrder = new Map(categories.map((row, index) => [row.id, index]));
    return [...items].sort((a, b) => (categoryOrder.get(a.category_id) ?? categories.length) - (categoryOrder.get(b.category_id) ?? categories.length) || Number(a.menu_display_order || 0) - Number(b.menu_display_order || 0) || String(a.name || "").localeCompare(String(b.name || "")));
  }, [items, categories]);
  const waiterDishMatches = useMemo(() => {
    const query = waiterDishQuery.trim().toLocaleLowerCase();
    return items
      .filter((item) => item.is_published !== false)
      .filter((item) => {
        if (!query) return true;
        const category =
          categories.find((row) => row.id === item.category_id)?.name || "";
        return [item.name, item.sku, category].some((value) =>
          String(value || "").toLocaleLowerCase().includes(query),
        );
      })
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      )
      .slice(0, 60);
  }, [items, categories, waiterDishQuery]);

  const displayOrders = useMemo(
    () => groupActiveOrdersForDisplay(orders),
    [orders],
  );

  const waiterActiveOrders = useMemo(
    () =>
      displayOrders.filter(
        (order) =>
          isOrderSelectable(order) && orderFulfillmentType(order) === "offline",
      ),
    [displayOrders],
  );

  const selectedWaiterOrder = useMemo(
    () =>
      waiterActiveOrders.find((order) => order.id === waiterOrderId) || null,
    [waiterActiveOrders, waiterOrderId],
  );
  const selectedQuickTable = useMemo(
    () =>
      restaurantTables.find(
        (table) =>
          String(table.table_number) === String(waiterForm.table_number),
      ) || null,
    [restaurantTables, waiterForm.table_number],
  );

  const fillWaiterFromOrder = useCallback((order) => {
    if (!order) return;
    setManualWaiterMode(false);
    setWaiterOrderId(order.id);
    setWaiterForm((prev) => ({
      ...prev,
      phone: order.shopper_phone || prev.phone,
      name: order.shopper_name || prev.name,
      table_number:
        order.table_number || formatOrderDelivery(order) || prev.table_number,
      assignments: order.dining_assignments?.length
        ? order.dining_assignments.map((seat) => ({
            table_number: String(seat.table_number),
            seat_number: Number(seat.seat_number),
          }))
        : prev.assignments,
    }));
  }, []);

  const toggleQuickSeat = (tableNumber, seatNumber) => {
    setWaiterForm((prev) => {
      const exists = (prev.assignments || []).some(
        (seat) =>
          String(seat.table_number) === String(tableNumber) &&
          Number(seat.seat_number) === Number(seatNumber),
      );
      const assignments = exists
        ? prev.assignments.filter(
            (seat) =>
              !(
                String(seat.table_number) === String(tableNumber) &&
                Number(seat.seat_number) === Number(seatNumber)
              ),
          )
        : [
            ...(prev.assignments || []),
            {
              table_number: String(tableNumber),
              seat_number: Number(seatNumber),
            },
          ];
      return { ...prev, assignments };
    });
  };

  useEffect(() => {
    if (manualWaiterMode || activeDiningSession || waiterOrderId || !waiterActiveOrders.length) return;
    const hasManualCustomer =
      waiterForm.phone.trim() || waiterForm.table_number.trim();
    if (hasManualCustomer) return;
    fillWaiterFromOrder(waiterActiveOrders[0]);
  }, [
    fillWaiterFromOrder,
    waiterActiveOrders,
    manualWaiterMode,
    activeDiningSession,
    waiterForm.phone,
    waiterForm.table_number,
    waiterOrderId,
  ]);

  const orderTypeCounts = useMemo(
    () => ({
      offline: displayOrders.filter(
        (order) => orderFulfillmentType(order) === "offline",
      ).length,
      online: displayOrders.filter(
        (order) => orderFulfillmentType(order) !== "offline",
      ).length,
    }),
    [displayOrders],
  );

  const attentionTypeCounts = useMemo(() => {
    const attentionIds = new Set(attentionOrderIds);
    return displayOrders.reduce(
      (acc, order) => {
        const ids = order._sourceOrderIds || [order.id];
        if (!ids.some((id) => attentionIds.has(id))) return acc;
        const type =
          orderFulfillmentType(order) === "offline" ? "offline" : "online";
        acc[type] += 1;
        return acc;
      },
      { offline: 0, online: 0 },
    );
  }, [attentionOrderIds, displayOrders]);

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    return displayOrders.filter((order) => {
      if (orderTypeFilter !== "all") {
        const type =
          orderFulfillmentType(order) === "offline" ? "offline" : "online";
        if (type !== orderTypeFilter) return false;
      }
      if (orderTodayOnly && !isOrderToday(order.created_at)) return false;
      if (orderStatusFilter === "not_completed" && order.status === "completed")
        return false;
      else if (
        orderStatusFilter !== "all" &&
        orderStatusFilter !== "not_completed" &&
        order.status !== orderStatusFilter
      )
        return false;
      return orderMatchesSearch(order, q);
    });
  }, [
    displayOrders,
    orderSearch,
    orderStatusFilter,
    orderTodayOnly,
    orderTypeFilter,
  ]);

  const selectableFilteredOrders = useMemo(
    () => filteredOrders.filter(isOrderSelectable),
    [filteredOrders],
  );

  const selectedOrders = useMemo(
    () =>
      displayOrders.filter(
        (order) =>
          selectedOrderIds.includes(order.id) && isOrderSelectable(order),
      ),
    [displayOrders, selectedOrderIds],
  );

  const selectedTotal = useMemo(
    () =>
      selectedOrders.reduce((sum, order) => sum + Number(order.total || 0), 0),
    [selectedOrders],
  );

  const allVisibleSelected =
    selectableFilteredOrders.length > 0 &&
    selectableFilteredOrders.every((order) =>
      selectedOrderIds.includes(order.id),
    );

  const activeOrderSummaries = useMemo(() => {
    const attentionIds = new Set(attentionOrderIds);
    return selectableFilteredOrders.map((order) => ({
      id: order.id,
      label:
        orderFulfillmentType(order) === "offline"
          ? formatOrderDelivery(order)
          : order.shopper_name || order.order_no,
      customer:
        order.shopper_name ||
        order.shopper_phone ||
        order.shopper_email ||
        "Guest",
      total: Number(order.total || 0),
      itemCount: (order.items || []).reduce(
        (sum, item) => sum + Number(item.qty || 0),
        0,
      ),
      newItemCount: (order.items || []).filter(
        (item) => (item.item_status || "new") === "new",
      ).length,
      needsAttention: (order._sourceOrderIds || [order.id]).some((id) =>
        attentionIds.has(id),
      ),
      status: order.status,
      opened: minutesAgo(order.created_at),
    }));
  }, [attentionOrderIds, selectableFilteredOrders]);

  const filteredDiningSessions = useMemo(() => {
    const query = diningPartySearch.trim().toLowerCase();
    if (!query) return diningSessions;
    return diningSessions.filter((session) =>
      [session.customer_name, session.customer_mobile, session.seat_label, session.code, session.table_number]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [diningPartySearch, diningSessions]);

  const waitingDiningSessions = useMemo(() => {
    if (orderTypeFilter === "online") return [];
    const query = orderSearch.trim().toLowerCase();
    return diningSessions.filter((session) => {
      if (session.order_id) return false;
      if (!query) return true;
      return [session.customer_name, session.customer_mobile, session.seat_label, session.code, session.table_number]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [diningSessions, orderSearch, orderTypeFilter]);

  const selectDiningSessionForOrder = (session) => {
    setManualWaiterMode(false);
    setActiveDiningSession(session);
    setWaiterOrderId(session.order_id || "");
    setWaiterForm((prev) => ({
      ...prev,
      phone: session.customer_mobile || "",
      name: session.customer_name || "",
      table_number: String(session.assignments?.[0]?.table_number || session.table_number || ""),
      assignments: (session.assignments || []).map((seat) => ({
        table_number: String(seat.table_number),
        seat_number: Number(seat.seat_number),
      })),
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const toggleOrderSelected = (orderId) => {
    const order = displayOrders.find((o) => o.id === orderId);
    if (!order || !isOrderSelectable(order)) return;
    setSelectedOrderIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId],
    );
  };

  const jumpToOrder = (orderId) => {
    const order = displayOrders.find((o) => o.id === orderId);
    if (
      order &&
      orderFulfillmentType(order) === "offline" &&
      isOrderSelectable(order)
    ) {
      fillWaiterFromOrder(order);
    }
    const sourceIds = order?._sourceOrderIds || [orderId];
    setAttentionOrderIds((prev) =>
      prev.filter((id) => !sourceIds.includes(id)),
    );
    const el = document.getElementById(`order-${orderId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const switchOrderType = (type) => {
    setOrderTypeFilter(type);
    if (type === "all") {
      setAttentionOrderIds([]);
      return;
    }
    const idsForType = new Set(
      displayOrders
        .filter(
          (order) =>
            (orderFulfillmentType(order) === "offline"
              ? "offline"
              : "online") === type,
        )
        .flatMap((order) => order._sourceOrderIds || [order.id]),
    );
    setAttentionOrderIds((prev) => prev.filter((id) => !idsForType.has(id)));
  };

  const toggleSelectAllVisible = (checked) => {
    if (!checked) {
      const visible = new Set(
        selectableFilteredOrders.map((order) => order.id),
      );
      setSelectedOrderIds((prev) => prev.filter((id) => !visible.has(id)));
      return;
    }
    const merged = new Set([
      ...selectedOrderIds,
      ...selectableFilteredOrders.map((order) => order.id),
    ]);
    setSelectedOrderIds([...merged]);
  };

  const selectCustomerVisibleOrders = (customerKey) => {
    const ids = selectableFilteredOrders
      .filter((order) => orderCustomerKey(order) === customerKey)
      .map((order) => order.id);
    const merged = new Set([...selectedOrderIds, ...ids]);
    setSelectedOrderIds([...merged]);
  };

  const orderStatusClass = (status) =>
    ({
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      confirmed: "bg-blue-50 text-blue-700 border-blue-200",
      shipped: "bg-purple-50 text-purple-700 border-purple-200",
      delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
      completed: "bg-slate-100 text-slate-700 border-slate-300",
      cancelled: "bg-red-50 text-red-700 border-red-200",
    })[status] || "bg-gray-50 text-gray-700 border-gray-200";

  const itemStatusClass = (status) =>
    ({
      new: "border-2 border-dashed border-amber-700 bg-amber-100 text-slate-950",
      confirmed: "border-2 border-solid border-blue-700 bg-blue-100 text-slate-950",
      preparing: "border-2 border-dotted border-fuchsia-800 bg-fuchsia-100 text-slate-950",
      furnished: "border-2 border-solid border-slate-950 bg-slate-800 text-white",
      cancelled: "border-2 border-dashed border-red-800 bg-white text-red-900",
    })[status || "new"] || "border-2 border-gray-500 bg-gray-100 text-slate-950";

  return (
    <div>
      <Tabs
        value={activeHotelTab}
        onValueChange={changeHotelTab}
        className={activeHotelTab === "basic-orders" ? "space-y-0 md:space-y-6" : "space-y-6"}
      >
        {withPageHeader ? (
          <div className={activeHotelTab === "basic-orders" ? "hidden md:block" : ""}>
          <PageHeader
            overline="Hotel / Restaurant"
            title={isBasicMenu ? "Digital menu." : "Digital menu & orders."}
            subtitle={isBasicMenu ? "Manage the read-only QR menu, categories and appearance." : "Manage menu items, tables, waiter routing, kitchen status and customer orders."}
          >
            <TabsList className="flex h-auto w-fit max-w-full flex-nowrap justify-end gap-2 overflow-x-auto rounded-sm bg-[#F3F4F6] p-2">
              <TabsTrigger
                value="hotel"
                className={isBasicMenu ? "hidden" : "rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white"}
              >
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Hotel Ops
              </TabsTrigger>
              <TabsTrigger
                value="products"
                className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white"
              >
                <Package className="w-3.5 h-3.5 mr-1.5" /> Menu Items
              </TabsTrigger>
              <TabsTrigger
                value="categories"
                className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white"
              >
                <FolderTree className="w-3.5 h-3.5 mr-1.5" /> Menu Categories
              </TabsTrigger>
              <TabsTrigger
                value="banners"
                className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white"
              >
                <Images className="w-3.5 h-3.5 mr-1.5" /> Banners
              </TabsTrigger>
              <TabsTrigger
                value="orders"
                className={isBasicMenu ? "hidden" : "rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white"}
              >
                <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Orders
              </TabsTrigger>
              {basicMenuLevel2 && <TabsTrigger value="basic-orders" className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white"><ShoppingCart className="mr-1.5 h-3.5 w-3.5" /> Level 2 Orders</TabsTrigger>}
              <TabsTrigger
                value="settings"
                className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white"
              >
                <Settings className="w-3.5 h-3.5 mr-1.5" /> Settings
              </TabsTrigger>
            </TabsList>
          </PageHeader>
          </div>
        ) : null}

        <TabsContent value="hotel">
          <HotelRestaurantOps />
        </TabsContent>

        <TabsContent value="products">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] mb-1">
                Digital menu
              </div>
              <h3 className="font-display text-2xl font-extrabold tracking-tight">
                Menu Items
              </h3>
            </div>
            <Button
              onClick={() => {
                setEditing(null);
                setForm(empty);
                setOpen(true);
              }}
              className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> New Menu Item
            </Button>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                    Menu item
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                    Category
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                    SKU
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280] text-right">Order</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280] text-right">
                    Price
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280] text-right">
                    Availability
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-10 text-sm text-[#6B7280]"
                    >
                      No products yet.
                    </TableCell>
                  </TableRow>
                )}
                {sortedMenuItems.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {(p.image_urls?.[0] || p.image_url) && (
                          <img
                            src={resolveMediaUrl(
                              p.image_urls?.[0] || p.image_url,
                            )}
                            alt=""
                            className="w-10 h-10 object-cover rounded border"
                          />
                        )}
                        <div>
                          <div className="font-semibold">{p.name}</div>
                          <div className="text-xs text-[#6B7280] line-clamp-1">
                            {p.description}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {categoryName(p.category_id)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {p.sku || "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{Number(p.menu_display_order || 0)}</TableCell>
                    <TableCell className="text-right font-mono">
                      ₹ {Number(p.price).toLocaleString()}
                    </TableCell>
                    <TableCell
                      className={`text-right text-xs font-bold ${p.is_published !== false ? "text-emerald-700" : "text-red-600"}`}
                    >
                      {p.is_published !== false ? "AVAILABLE" : "SOLD OUT"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditing(p);
                          setForm({
                            ...empty,
                            ...p,
                            stock: displayStock(p.stock),
                            image_urls: p.image_urls?.length
                              ? p.image_urls
                              : p.image_url
                                ? [p.image_url]
                                : [],
                            tags: Array.isArray(p.tags)
                              ? p.tags.join(", ")
                              : p.tags || "",
                            category_id: p.category_id || "",
                          });
                          setOpen(true);
                        }}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(p.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="categories">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] mb-1">
                Catalog
              </div>
              <h3 className="font-display text-2xl font-extrabold tracking-tight">
                Categories
              </h3>
            </div>
            <Button
              onClick={() => {
                setEditingCat(null);
                setCatForm(emptyCat);
                setCatOpen(true);
              }}
              className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> New Category
            </Button>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9FAFB]">
                  <TableHead>Name</TableHead>
                  <TableHead>Sort</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center py-10 text-sm text-[#6B7280]"
                    >
                      No categories yet.
                    </TableCell>
                  </TableRow>
                )}
                {categories.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-semibold">{c.name}</TableCell>
                    <TableCell>{c.sort_order}</TableCell>
                    <TableCell>
                      {c.is_active !== false ? "Active" : "Hidden"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditingCat(c);
                          setCatForm({
                            name: c.name,
                            sort_order: c.sort_order || 0,
                            is_active: c.is_active !== false,
                          });
                          setCatOpen(true);
                        }}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeCategory(c.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="banners">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] mb-1">
                Homepage
              </div>
              <h3 className="font-display text-2xl font-extrabold tracking-tight">
                Banner slider
              </h3>
              <p className="text-sm text-[#6B7280] mt-1">
                Upload images that rotate on your public store homepage. Slides
                show one after another automatically.
              </p>
            </div>
            <label
              className={`inline-flex items-center gap-2 rounded-sm px-4 py-2 bg-[#111827] text-white text-sm cursor-pointer hover:bg-[#374151] ${uploadingBanner ? "opacity-60 pointer-events-none" : ""}`}
            >
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  uploadBannerImage(e.target.files?.[0]);
                  e.target.value = "";
                }}
                className="hidden"
              />
              <Plus className="w-4 h-4" />{" "}
              {uploadingBanner ? "Uploading…" : "Add banner · 1920 × 600 px"}
            </label>
          </div>

          {(profile.banner_slides || []).length === 0 ? (
            <div className="border border-dashed border-[#D1D5DB] rounded-2xl p-10 text-center text-sm text-[#6B7280]">
              No banners yet. Upload images to create a sliding hero on your
              public store.
            </div>
          ) : (
            <div className="space-y-4">
              {(profile.banner_slides || []).map((slide, index) => (
                <div
                  key={slide.id}
                  className="bg-white border border-[#E5E7EB] rounded-sm p-4 grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-4 items-start"
                >
                  <div className="aspect-[21/9] rounded-md overflow-hidden bg-[#F3F4F6] border border-[#E5E7EB]">
                    {slide.image_url ? (
                      <img
                        src={resolveMediaUrl(slide.image_url)}
                        alt={slide.title || `Banner ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-[#9CA3AF]">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                        Title
                      </Label>
                      <Input
                        value={slide.title || ""}
                        onChange={(e) =>
                          updateBannerSlide(slide.id, { title: e.target.value })
                        }
                        className="rounded-sm mt-1"
                        placeholder="Summer sale — up to 40% off"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                        Subtitle
                      </Label>
                      <Input
                        value={slide.subtitle || ""}
                        onChange={(e) =>
                          updateBannerSlide(slide.id, {
                            subtitle: e.target.value,
                          })
                        }
                        className="rounded-sm mt-1"
                        placeholder="Limited time on bedding and decor"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                        Link (optional)
                      </Label>
                      <Input
                        value={slide.link_url || ""}
                        onChange={(e) =>
                          updateBannerSlide(slide.id, {
                            link_url: e.target.value,
                          })
                        }
                        className="rounded-sm mt-1 font-mono text-sm"
                        placeholder="search?q=pillows or product/abc123"
                      />
                      <p className="mt-1 text-[11px] text-[#9CA3AF]">
                        Use a search query, product path, or full URL for the
                        Shop now button.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={slide.is_active !== false}
                        onCheckedChange={(v) =>
                          updateBannerSlide(slide.id, { is_active: v })
                        }
                      />
                      <Label className="text-sm">Show on public store</Label>
                    </div>
                  </div>
                  <div className="flex md:flex-col gap-1">
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="rounded-sm"
                      disabled={index === 0}
                      onClick={() => moveBannerSlide(slide.id, -1)}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="rounded-sm"
                      disabled={
                        index === (profile.banner_slides || []).length - 1
                      }
                      onClick={() => moveBannerSlide(slide.id, 1)}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="text-red-600 rounded-sm"
                      onClick={() => removeBannerSlide(slide.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 flex items-center justify-between gap-4 border-t border-[#E5E7EB] pt-4">
            <p className="text-xs text-[#6B7280]">Save after adding, editing, reordering or removing banners.</p>
            <Button type="button" onClick={saveProfile} className="rounded-sm bg-[#111827] px-6 text-white hover:bg-[#374151]">
              <Save className="mr-2 h-4 w-4" /> Save banners
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280]">Sales</div>
              <h3 className="font-display text-2xl font-extrabold tracking-tight">Customer orders</h3>
              <p className="mt-1 text-sm text-[#6B7280]">Search by customer, select open orders (not completed) to total them, then mark the batch completed when done.</p>
            </div>
            {!!diningSessions.length && (
              <div className="flex items-center gap-2">
                <span className="bg-blue-100 px-2 py-1 text-xs font-black text-blue-800">{diningSessions.length} active parties</span>
                <Button type="button" size="sm" variant="outline" className="h-8 rounded-sm" onClick={() => setShowDiningParties((visible) => !visible)}>
                  {showDiningParties ? "Hide parties" : "Show parties"}
                </Button>
              </div>
            )}
          </div>
          <form
            onSubmit={createWaiterOrder}
            className="mb-4 rounded-sm border border-[#D1D5DB] bg-white p-4 shadow-sm"
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-[#111827] text-white">
                  <UserPlus className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-base font-extrabold text-[#111827]">
                    Quick table order
                  </div>
                  <div className="text-xs text-[#6B7280]">
                    {manualWaiterMode ? "Allot table seats first; add dishes after opening the customer." : "Add dishes to the selected customer bill."}
                  </div>
                </div>
              </div>
              <div className="hidden rounded-sm bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 sm:block">
                Hotel desk
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-10">
              <select
                value={activeDiningSession && !activeDiningSession.order_id ? `session:${activeDiningSession.id}` : waiterOrderId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  if (nextId.startsWith("session:")) {
                    const session = diningSessions.find((item) => item.id === nextId.slice(8));
                    if (session) selectDiningSessionForOrder(session);
                    return;
                  }
                  setWaiterOrderId(nextId);
                  if (!nextId) {
                    setManualWaiterMode(true);
                    setActiveDiningSession(null);
                    setWaiterForm(emptyWaiter);
                    return;
                  }
                  const order = waiterActiveOrders.find(
                    (item) => item.id === nextId,
                  );
                  setActiveDiningSession(
                    diningSessions.find(
                      (session) => session.id === order?.dining_session_id,
                    ) || null,
                  );
                  fillWaiterFromOrder(order);
                }}
                className="h-11 rounded-sm border border-[#D1D5DB] bg-[#F9FAFB] px-3 py-2 text-sm font-medium md:col-span-2"
              >
                <option value="">New / manual table</option>
                {diningSessions.filter((session) => !session.order_id).map((session) => {
                  const tableNumber = session.assignments?.[0]?.table_number || session.table_number || "";
                  return (
                    <option key={`session-${session.id}`} value={`session:${session.id}`}>
                      {tableNumber ? `T-${tableNumber}` : "Walk-in"} - {session.customer_name || session.customer_mobile || "Guest"} - waiting for order
                    </option>
                  );
                })}                {waiterActiveOrders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {formatOrderDelivery(order)} -{" "}
                    {order.shopper_name || order.shopper_phone || "Guest"} - ₹{" "}
                    {Number(order.total || 0).toLocaleString()}
                  </option>
                ))}
              </select>
              <Input
                value={waiterForm.phone}
                onChange={(e) =>
                  setWaiterForm({ ...waiterForm, phone: e.target.value })
                }
                placeholder="Mobile optional"
                className="h-11 rounded-sm md:col-span-1"
              />
              <Input
                value={waiterForm.name}
                onChange={(e) =>
                  setWaiterForm({ ...waiterForm, name: e.target.value })
                }
                placeholder="Name optional"
                className="h-11 rounded-sm md:col-span-1"
              />
              <select
                aria-label="Table"
                value={waiterForm.table_number}
                disabled={!!activeDiningSession}
                onChange={(e) =>
                  setWaiterForm({ ...waiterForm, table_number: e.target.value })
                }
                className="h-11 border border-[#D1D5DB] bg-white px-3 text-sm font-bold disabled:bg-slate-100 md:col-span-1"
              >
                <option value="">Select table</option>
                {restaurantTables
                  .filter((table) => table.is_active !== false)
                  .map((table) => (
                    <option key={table.id} value={table.table_number}>
                      {table.table_name || `Table ${table.table_number}`}
                    </option>
                  ))}
              </select>
              {manualWaiterMode ? <div className="flex h-11 items-center rounded-sm border border-amber-200 bg-amber-50 px-3 text-xs font-bold text-amber-800 md:col-span-5">Select the required seats below, then allot seats and generate the customer code.</div> : <>
              <div className="relative md:col-span-3">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400"
                />
                <input
                  type="search"
                  value={waiterDishQuery}
                  onFocus={() => setWaiterDishSearchOpen(true)}
                  onBlur={() =>
                    window.setTimeout(() => setWaiterDishSearchOpen(false), 120)
                  }
                  onChange={(event) => {
                    setWaiterDishQuery(event.target.value);
                    setWaiterDishSearchOpen(true);
                    setWaiterForm((prev) => ({ ...prev, product_id: "" }));
                  }}
                  placeholder="Search dish by name, category or SKU"
                  autoComplete="off"
                  aria-label="Search and select dish"
                  aria-expanded={waiterDishSearchOpen}
                  className="h-11 w-full rounded-sm border border-[#D1D5DB] bg-white pl-9 pr-3 text-sm outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600"
                />
                {waiterDishSearchOpen && (
                  <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-sm border border-slate-300 bg-white shadow-xl">
                    {waiterDishMatches.length ? (
                      waiterDishMatches.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            setWaiterForm((prev) => ({
                              ...prev,
                              product_id: item.id,
                            }));
                            setWaiterDishQuery(item.name || "");
                            setWaiterDishSearchOpen(false);
                          }}
                          className={`flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-emerald-50 ${
                            waiterForm.product_id === item.id
                              ? "bg-emerald-50 font-bold text-emerald-900"
                              : "text-slate-800"
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-semibold">
                              {item.name}
                            </span>
                            {(item.sku || item.category_id) && (
                              <span className="block truncate text-[11px] text-slate-500">
                                {[
                                  item.sku,
                                  categories.find(
                                    (row) => row.id === item.category_id,
                                  )?.name,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            )}
                          </span>
                          <span className="shrink-0 font-bold text-slate-900">
                            ₹ {Number(item.price || 0).toLocaleString()}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-4 text-center text-sm text-slate-500">
                        No matching dish found.
                      </div>
                    )}
                    {waiterDishMatches.length === 60 && (
                      <div className="sticky bottom-0 border-t border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold text-slate-600">
                        Showing the first 60 matches. Type more letters to narrow the list.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 md:col-span-2">
                <Input
                  type="number"
                  min="1"
                  value={waiterForm.qty}
                  onChange={(e) =>
                    setWaiterForm({ ...waiterForm, qty: e.target.value })
                  }
                  className="h-11 w-20 rounded-sm text-center font-bold"
                />
                <Button
                  type="submit"
                  disabled={waiterBusy}
                  className="h-11 flex-1 rounded-sm bg-[#111827] text-white hover:bg-[#374151]"
                >
                  {waiterBusy ? "Adding..." : "Add"}
                </Button>
              </div>
              </>}
            </div>
            {selectedQuickTable && !selectedWaiterOrder && (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3">
                <span className="mr-1 text-xs font-bold uppercase text-slate-600">
                  {selectedQuickTable.table_name ||
                    `Table ${selectedQuickTable.table_number}`}{" "}
                  seats
                </span>
                {Array.from(
                  { length: Number(selectedQuickTable.capacity || 0) },
                  (_, index) => index + 1,
                ).map((seat) => {
                  const occupied = (
                    selectedQuickTable.occupied_seats || []
                  ).includes(seat);
                  const selected = (waiterForm.assignments || []).some(
                    (item) =>
                      String(item.table_number) ===
                        String(selectedQuickTable.table_number) &&
                      Number(item.seat_number) === seat,
                  );
                  return (
                    <button
                      key={seat}
                      type="button"
                      disabled={
                        !!activeDiningSession || (occupied && !selected)
                      }
                      onClick={() =>
                        toggleQuickSeat(selectedQuickTable.table_number, seat)
                      }
                      className={`min-w-12 border px-3 py-2 text-sm font-black ${selected ? "border-blue-700 bg-blue-600 text-white" : occupied ? "cursor-not-allowed border-red-200 bg-red-50 text-red-500" : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"}`}
                    >
                      S{seat}
                      <span className="ml-1 text-[10px] font-semibold">
                        {selected ? "✓" : occupied ? "Busy" : "Free"}
                      </span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  disabled={!!activeDiningSession}
                  onClick={() => {
                    const freeSeats = Array.from(
                      { length: Number(selectedQuickTable.capacity || 0) },
                      (_, index) => index + 1,
                    ).filter(
                      (seat) =>
                        !(selectedQuickTable.occupied_seats || []).includes(
                          seat,
                        ),
                    );
                    setWaiterForm((prev) => ({
                      ...prev,
                      assignments: [
                        ...(prev.assignments || []).filter(
                          (item) =>
                            String(item.table_number) !==
                            String(selectedQuickTable.table_number),
                        ),
                        ...freeSeats.map((seat) => ({
                          table_number: String(selectedQuickTable.table_number),
                          seat_number: seat,
                        })),
                      ],
                    }));
                  }}
                  className="border border-blue-300 bg-blue-50 px-3 py-2 text-xs font-bold text-blue-800 disabled:opacity-50"
                >
                  Select all free
                </button>
                {!!waiterForm.assignments?.length && (
                  <span className="ml-auto text-xs font-bold text-blue-800">
                    Family seats:{" "}
                    {waiterForm.assignments
                      .map(
                        (item) => `T${item.table_number}-S${item.seat_number}`,
                      )
                      .join(", ")}
                  </span>
                )}
              </div>
            )}
            {!selectedWaiterOrder && !!waiterForm.assignments?.length && (
              <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-3">
                {activeDiningSession ? (
                  <>
                    <div className="border border-amber-300 bg-amber-50 px-4 py-2">
                      <span className="mr-3 text-xs font-bold uppercase text-amber-800">
                        Customer code
                      </span>
                      <span className="text-2xl font-black tracking-[0.22em] text-blue-900">
                        {activeDiningSession.code}
                      </span>
                      <span className="ml-3 text-xs font-semibold text-slate-600">
                        Valid until payment
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            `${window.location.origin}/dine/${encodeURIComponent(bookingSlug)}`,
                          );
                          toast.success("Customer ordering link copied");
                        } catch {
                          toast.error("Could not copy the customer link");
                        }
                      }}
                    >
                      Copy customer link
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="text-red-700"
                      onClick={() =>
                        cancelDiningSession(activeDiningSession.id)
                      }
                    >
                      Cancel allotment
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    disabled={waiterBusy}
                    className="bg-amber-500 text-slate-950 hover:bg-amber-600"
                    onClick={generateDiningCode}
                  >
                    <QrCode className="mr-2 h-4 w-4" />
                    Allot seats &amp; generate 4-digit code
                  </Button>
                )}
              </div>
            )}
            {selectedWaiterOrder && (
              <div className="mt-2 text-xs font-medium text-[#047857]">
                Adding to active bill:{" "}
                {formatOrderDelivery(selectedWaiterOrder)} for{" "}
                {selectedWaiterOrder.shopper_name ||
                  selectedWaiterOrder.shopper_phone ||
                  "Guest"}
                .
              </div>
            )}
          </form>

          {!!diningSessions.length && showDiningParties && (
            <div className="mb-4 border border-slate-300 bg-white">
              <div className="hidden">
                <div className="flex flex-wrap items-center gap-2">
                  <b>Active dining parties</b>
                  <span className="text-xs text-slate-500">Codes remain active until payment</span>
                  <span className="bg-blue-100 px-2 py-1 text-xs font-black text-blue-800">{diningSessions.length} active</span>
                </div>
                <Button type="button" size="sm" variant="outline" className="h-8 rounded-sm" onClick={() => setShowDiningParties((visible) => !visible)}>{showDiningParties ? "Hide parties" : "Show parties"}</Button>
              </div>
              {showDiningParties && (<div className="p-3">
                <div className="relative mb-3"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" /><Input value={diningPartySearch} onChange={(event) => setDiningPartySearch(event.target.value)} placeholder="Search name, mobile, table, seat or code" className="h-9 rounded-sm pl-9" /></div>
                <div className="max-h-64 overflow-auto border border-slate-200">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Seats</th>
                      <th>Code</th>
                      <th>Bill</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDiningSessions.map((session) => (
                      <tr key={session.id}>
                        <td>
                          <b>{session.customer_name || "Guest"}</b>
                          <div className="text-xs text-slate-500">
                            {session.customer_mobile}
                          </div>
                        </td>
                        <td>{session.seat_label}</td>
                        <td>
                          <span className="text-lg font-black tracking-[0.16em] text-blue-800">
                            {session.code}
                          </span>
                        </td>
                        <td>
                          {session.order_id
                            ? "Order active"
                            : "Waiting for order"}
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setManualWaiterMode(false);
                                setActiveDiningSession(session);
                                setWaiterOrderId(session.order_id || "");
                                setWaiterForm((prev) => ({
                                  ...prev,
                                  phone: session.customer_mobile || "",
                                  name: session.customer_name || "",
                                  table_number: String(
                                    session.assignments?.[0]?.table_number ||
                                      session.table_number ||
                                      "",
                                  ),
                                  assignments: (session.assignments || []).map(
                                    (seat) => ({
                                      table_number: String(seat.table_number),
                                      seat_number: Number(seat.seat_number),
                                    }),
                                  ),
                                }));
                              }}
                            >
                              Use / add order
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                navigator.clipboard.writeText(session.code)
                              }
                            >
                              Copy code
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-red-700"
                              onClick={() => cancelDiningSession(session.id)}
                            >
                              Vacate
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
    </div>)}        </div>
          )}

          <div className="mb-4 grid grid-cols-1 gap-3 rounded-sm border border-[#E5E7EB] bg-white p-3 xl:grid-cols-[auto_minmax(0,1fr)]">
            <div className="flex flex-wrap items-center gap-2">
              {[
                {
                  key: "offline",
                  label: "In-store",
                  count: orderTypeCounts.offline,
                  alert: attentionTypeCounts.offline,
                },
                {
                  key: "online",
                  label: "Online",
                  count: orderTypeCounts.online,
                  alert: attentionTypeCounts.online,
                },
                {
                  key: "all",
                  label: "All",
                  count: displayOrders.length,
                  alert:
                    attentionTypeCounts.offline + attentionTypeCounts.online,
                },
              ].map((tab) => {
                const active = orderTypeFilter === tab.key;
                const hasAlert = tab.alert > 0 && !active;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => switchOrderType(tab.key)}
                    className={`rounded-sm border px-4 py-2 text-sm font-bold transition ${
                      active
                        ? "border-[#111827] bg-[#111827] text-white"
                        : hasAlert
                          ? "border-amber-300 bg-amber-100 text-amber-900 shadow-sm"
                          : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#111827]"
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`ml-2 rounded-sm px-1.5 py-0.5 text-[10px] ${active ? "bg-white/15 text-white" : "bg-[#F3F4F6] text-[#6B7280]"}`}
                    >
                      {tab.count}
                    </span>
                    {hasAlert && (
                      <span className="ml-2 rounded-sm bg-amber-500 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white">
                        New
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search table, item, customer, phone, status, or order no."
                  className="rounded-sm pl-9"
                />
              </div>
              <Select
                value={orderStatusFilter}
                onValueChange={setOrderStatusFilter}
              >
                <SelectTrigger className="rounded-sm min-w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="not_completed">Not completed</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <label className="inline-flex items-center gap-2 rounded-sm border border-[#E5E7EB] px-3 py-2 text-sm cursor-pointer hover:bg-[#F9FAFB]">
                <Checkbox
                  checked={orderTodayOnly}
                  onCheckedChange={(v) => setOrderTodayOnly(v === true)}
                />
                Today only
              </label>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 text-sm xl:col-span-2">
              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={(v) => toggleSelectAllVisible(v === true)}
                    disabled={selectableFilteredOrders.length === 0}
                  />
                  Select all open ({selectableFilteredOrders.length})
                </label>
                <span className="text-[#6B7280]">
                  Showing {filteredOrders.length} current bill
                  {filteredOrders.length === 1 ? "" : "s"} from {orders.length}{" "}
                  order entr{orders.length === 1 ? "y" : "ies"}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => {
                    setOrderStatusFilter("not_completed");
                    setOrderTodayOnly(true);
                  }}
                >
                  Today · open
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => {
                    setOrderStatusFilter("delivered");
                    setOrderTodayOnly(true);
                  }}
                >
                  Today · delivered
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => {
                    setOrderSearch("");
                    setOrderStatusFilter("all");
                    setOrderTodayOnly(false);
                  }}
                >
                  Clear filters
                </Button>
              </div>
            </div>
          </div>

          {selectedOrderIds.length > 0 && (
            <div className="bg-[#111827] text-white rounded-sm px-4 py-3 mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-white/70">
                  Selected open orders
                </div>
                <div className="font-display text-xl font-bold">
                  {selectedOrders.length} order
                  {selectedOrders.length === 1 ? "" : "s"} · ₹{" "}
                  {selectedTotal.toLocaleString()}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white"
                  disabled={completingSelected}
                  onClick={completeSelectedOrders}
                >
                  {completingSelected ? "Completing…" : "Mark completed"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm bg-white/10 text-white border-white/20 hover:bg-white/20"
                  onClick={() => setSelectedOrderIds([])}
                >
                  Clear selection
                </Button>
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="hidden grid-cols-[42px_120px_minmax(180px,1fr)_110px_130px_105px] items-center gap-3 bg-[#111827] px-3 py-2 text-[9px] font-black uppercase tracking-wider text-white/70 md:grid">
              <span>Select</span><span>Table / Guest</span><span>Customer</span><span>Ordered items</span><span>Amount / Status</span><span className="text-right">Process</span>
            </div>
            {orders.length === 0 && waitingDiningSessions.length === 0 && (
              <div className="bg-white border border-[#E5E7EB] rounded-sm p-10 text-center text-sm text-[#6B7280]">
                No orders yet.
              </div>
            )}
            {orders.length > 0 && filteredOrders.length === 0 && waitingDiningSessions.length === 0 && (
              <div className="bg-white border border-[#E5E7EB] rounded-sm p-10 text-center text-sm text-[#6B7280]">
                No orders match your filters.
              </div>
            )}
            {waitingDiningSessions.map((session, sessionIndex) => {
              const tableNumber = session.assignments?.[0]?.table_number || session.table_number || "";
              const compactTable = tableNumber ? `T-${tableNumber}` : "Walk-in";
              return (
                <div key={`waiting-${session.id}`} className={`border-l-4 border-l-amber-400 px-3 py-2 ${sessionIndex % 2 === 0 ? "bg-amber-50/60" : "bg-white"}`}>
                  <div className="grid grid-cols-[32px_110px_minmax(120px,180px)_minmax(240px,1fr)_150px_110px] items-center gap-3">
                    <div />
                    <div><b className="block text-sm text-[#111827]">{compactTable}</b><span className="text-[10px] font-bold uppercase text-[#6B7280]">Seats allotted</span></div>
                    <div className="min-w-0"><b className="block text-sm text-[#111827]">{session.customer_name || "Guest"}</b><span className="block truncate text-[10px] text-[#94A3B8]">{session.customer_mobile || `Code ${session.code}`}</span></div>
                    <div className="min-w-0"><span className="rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800">{session.seat_label || "Seats allotted"}</span></div>
                    <div><b className="block font-mono text-sm text-[#111827]">{"\u20B9"}0</b><span className="inline-block rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-800">Waiting for order</span></div>
                    <div className="text-right"><button type="button" onClick={() => selectDiningSessionForOrder(session)} className="text-[10px] font-black uppercase text-[#103F35] hover:underline">Add order</button></div>
                  </div>
                </div>
              );
            })}            {filteredOrders.map((order, orderIndex) => {
              const customerKey = orderCustomerKey(order);
              const customerLabel = orderCustomerLabel(order);
              const sameCustomerSelectableCount =
                selectableFilteredOrders.filter(
                  (o) => orderCustomerKey(o) === customerKey,
                ).length;
              const selectable = isOrderSelectable(order);
              const isSelected =
                selectable && selectedOrderIds.includes(order.id);
              const contentIndent = selectable ? "ml-7" : "";
              const isOfflineOrder = orderFulfillmentType(order) === "offline";
              const itemCount = orderItemCount(order);
              const pendingCount = orderPendingItemCount(order);
              const openTime = minutesAgo(order.created_at);
              const billLabel = orderBillLabel(order);
              const compactBillLabel = /^table\s+/i.test(billLabel)
                ? billLabel.replace(/^table\s+/i, "T-")
                : billLabel;
              const isExpanded = expandedAdvancedOrderId === order.id;

              return (
                <div
                  id={`order-${order.id}`}
                  key={order.id}
                  className={`scroll-mt-24 overflow-hidden border-b border-slate-200 bg-white last:border-b-0 ${isSelected ? "ring-2 ring-inset ring-[#111827]/15" : ""}`}
                >
                  <div
                    className={`border-l-4 px-3 py-2 ${isOfflineOrder ? "border-l-amber-500" : "border-l-blue-500"} ${isSelected ? "bg-blue-50" : orderIndex % 2 === 0 ? "bg-white hover:bg-slate-50" : "bg-slate-50 hover:bg-slate-100"}`}
                  >
                    <div className="grid grid-cols-[32px_110px_minmax(120px,180px)_minmax(240px,1fr)_150px_110px] items-center gap-3">
                      <div>{selectable ? <Checkbox checked={isSelected} onCheckedChange={() => toggleOrderSelected(order.id)} /> : null}</div>
                      <div>
                        <b className="block text-sm text-[#111827]">{compactBillLabel}</b>
                        <span className="text-[10px] font-bold uppercase text-[#6B7280]">{isOfflineOrder ? "In-store" : "Online"}</span>
                      </div>
                      <div className="min-w-0">
                        <b className="block text-sm text-[#111827]">{customerLabel}</b>
                        <span className="block truncate text-[10px] text-[#94A3B8]">{order.order_no || formatOrderDelivery(order)}</span>
                      </div>
                      <div className="flex min-w-0 flex-wrap gap-1">
                        {(order.items || []).map((item, itemIndex) => {
                          const status = item.item_status || "new";
                          return (
                            <span key={item._item_ref || item.line_id || itemIndex} className={`rounded-sm border px-1.5 py-0.5 text-[11px] font-semibold ${itemStatusClass(status)}`} title={`${item.name}: ${status}`}>
                              {Number(item.qty || 0)}x {item.name}
                            </span>
                          );
                        })}
                        {!itemCount && <span className="text-xs text-[#94A3B8]">No items</span>}
                      </div>
                      <div>
                        <b className="block font-mono text-sm text-[#111827]">{"\u20B9"}{Number(order.total || 0).toLocaleString()}</b>
                        <span className={`inline-block rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase ${orderStatusClass(order.status)}`}>{order.status}</span>
                        {pendingCount > 0 && <span className="ml-1 text-[9px] font-black uppercase text-amber-700">{pendingCount} new</span>}
                        {openTime && <span className="mt-0.5 block text-[10px] text-[#6B7280]">{openTime}</span>}
                      </div>
                      <div className="text-right">
                        <button type="button" onClick={() => setExpandedAdvancedOrderId(isExpanded ? "" : order.id)} className="text-[10px] font-black uppercase text-[#103F35] hover:underline">
                          {isExpanded ? "Close details" : "Manage order"}
                        </button>
                        {sameCustomerSelectableCount > 1 && <button type="button" className="mt-1 block w-full text-[10px] font-semibold text-[#007185] hover:underline" onClick={() => selectCustomerVisibleOrders(customerKey)}>Select all {sameCustomerSelectableCount}</button>}
                      </div>
                    </div>
                  </div>
                  {isExpanded && <>
                  <div className={`hidden ${contentIndent}`}>
                    {(order.items || []).map((item, i) => (
                      <div key={i} className="flex justify-between">
                        <span>
                          {item.name} × {item.qty}
                        </span>
                        <span>
                          ₹ {Number(item.line_total || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div
                    className={`mx-3 divide-y divide-[#E5E7EB] overflow-hidden rounded-t border border-[#D1D5DB] bg-white text-sm ${contentIndent}`}
                  >
                    <div className="flex items-center justify-between gap-3 bg-[#F3F4F6] px-3 py-2">
                      <div className="min-w-0"><b className="text-[#111827]">Order {orderIndex + 1}</b><span className="ml-2 text-xs text-[#94A3B8]">All additions combined</span></div>
                      <b className="shrink-0 text-xs uppercase tracking-wide text-[#111827]">{order.status}</b>
                    </div>
                    <div className="hidden grid-cols-[minmax(220px,1fr)_90px_190px_110px_190px] items-center gap-3 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-[#6B7280] md:grid">
                      <span>Menu item</span><span>Amount</span><span>Time</span><span>Status</span><span className="text-right">Action</span>
                    </div>
                    {(order.items || []).map((item, i) => {
                      const itemStatus = item.item_status || "new";
                      const itemRef =
                        item._item_ref || item.line_id || String(i);
                      const itemOrderId = item._order_id || order.id;
                      const statusTime =
                        item[`${itemStatus}_at`] || item.item_status_updated_at;
                      return (
                        <div
                          key={itemRef}
                          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 py-2 md:grid-cols-[minmax(220px,1fr)_90px_190px_110px_190px]"
                        >
                          <div className="min-w-0 truncate font-bold text-[#111827]">
                            {item.name}{" "}
                            <span className="text-[#6B7280]">× {item.qty}</span>
                          </div>
                          <span className="font-mono text-xs font-bold text-[#374151]">
                            ₹{Number(item.line_total || 0).toLocaleString()}
                          </span>
                          <div className="hidden truncate text-[11px] text-[#6B7280] md:block">
                            {item.ordered_at
                              ? `${formatShortTime(item.ordered_at)} · ${minutesAgo(item.ordered_at)}`
                              : "—"}
                            {statusTime && itemStatus !== "new"
                              ? ` · ${formatShortTime(statusTime)}`
                              : ""}
                          </div>
                          <span
                            className={`hidden rounded-sm border px-2 py-1 text-center text-[10px] font-bold uppercase md:block ${itemStatusClass(itemStatus)}`}
                          >
                            {itemStatus}
                          </span>
                          {isOrderSelectable(order) &&
                            itemStatus !== "cancelled" && (
                              <div className="col-span-2 flex justify-end gap-2 md:col-span-1">
                                {itemStatus === "new" && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="h-8 rounded-sm"
                                      onClick={() =>
                                        updateOrderItemStatus(
                                          itemOrderId,
                                          itemRef,
                                          "confirmed",
                                        )
                                      }
                                    >
                                      Confirm
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8 rounded-sm text-red-600"
                                      onClick={() =>
                                        updateOrderItemStatus(
                                          itemOrderId,
                                          itemRef,
                                          "cancelled",
                                        )
                                      }
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                )}
                                {itemStatus === "confirmed" && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="h-8 rounded-sm bg-purple-600 hover:bg-purple-700"
                                      onClick={() =>
                                        updateOrderItemStatus(
                                          itemOrderId,
                                          itemRef,
                                          "preparing",
                                        )
                                      }
                                    >
                                      Preparing
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8 rounded-sm text-red-600"
                                      onClick={() =>
                                        updateOrderItemStatus(
                                          itemOrderId,
                                          itemRef,
                                          "cancelled",
                                        )
                                      }
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                )}
                                {itemStatus === "preparing" && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-700"
                                      onClick={() =>
                                        updateOrderItemStatus(
                                          itemOrderId,
                                          itemRef,
                                          "furnished",
                                        )
                                      }
                                    >
                                      Furnished
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8 rounded-sm text-red-600"
                                      onClick={() =>
                                        updateOrderItemStatus(
                                          itemOrderId,
                                          itemRef,
                                          "cancelled",
                                        )
                                      }
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                )}
                              </div>
                            )}
                          {(!isOrderSelectable(order) ||
                            itemStatus === "cancelled") && (
                            <span className="hidden text-right text-xs text-[#6B7280] md:block">
                              —
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div
                    className={`mx-3 mb-3 flex flex-wrap items-center justify-between gap-3 rounded-b border border-t-0 border-[#D1D5DB] bg-[#F9FAFB] px-3 py-2 ${contentIndent}`}
                  >
                    <b className="font-mono text-sm text-[#111827]">Combined total: {"\u20B9"}{Number(order.total || 0).toLocaleString()}</b>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={order.status !== "completed"}
                        onClick={() => {
                          if (order.status !== "completed") return;
                          setBillOrder(order);
                        }}
                        title={order.status === "completed" ? "Print final bill" : "Complete billing before printing"}
                        className="rounded-sm"
                      >
                        <Printer className="mr-1.5 h-3.5 w-3.5" />
                        {order.status === "completed" ? "Print bill" : "Print after billing"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => shareBillToWhatsApp(order)}
                        className="rounded-sm text-emerald-700"
                      >
                        <MessageCircle className="mr-1.5 h-3.5 w-3.5" />{" "}
                        WhatsApp
                      </Button>
                      {order.status === "pending" && (
                        <Button
                          size="sm"
                          onClick={() => updateOrderStatus(order, "confirmed")}
                          className="rounded-sm"
                        >
                          Confirm
                        </Button>
                      )}
                      {order.status === "confirmed" && (
                        <Button
                          size="sm"
                          onClick={() => updateOrderStatus(order, "shipped")}
                          className="rounded-sm"
                        >
                          Mark shipped
                        </Button>
                      )}
                      {order.status === "shipped" && (
                        <Button
                          size="sm"
                          onClick={() => updateOrderStatus(order, "delivered")}
                          className="rounded-sm"
                        >
                          Mark delivered
                        </Button>
                      )}
                      {order.status === "delivered" && (
                        <Button
                          size="sm"
                          onClick={() => updateOrderStatus(order, "completed")}
                          className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                        >
                          Complete billing
                        </Button>
                      )}
                      {["pending", "confirmed", "shipped"].includes(
                        order.status,
                      ) && (
                        <Button
                          size="sm"
                          onClick={() => updateOrderStatus(order, "completed")}
                          className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                        >
                          Complete billing
                        </Button>
                      )}
                      {["pending", "confirmed"].includes(order.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateOrderStatus(order, "cancelled")}
                          className="rounded-sm text-red-600"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                  </>}
                </div>
              );
            })}
          </div>
        </TabsContent>

        {basicMenuLevel2 && <TabsContent value="basic-orders"><BasicMenuLevel2Orders /></TabsContent>}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
                {editing ? "Edit Menu Item" : "New Menu Item"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Name
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  SKU
                </Label>
                <Input
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  className="rounded-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Category
                </Label>
                <Select
                  value={form.category_id || "none"}
                  onValueChange={(v) =>
                    setForm({ ...form, category_id: v === "none" ? "" : v })
                  }
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No category</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Display order
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={form.menu_display_order || 0}
                  onChange={(e) => setForm({ ...form, menu_display_order: parseInt(e.target.value || "0", 10) })}
                  className="rounded-sm"
                />
                <p className="mt-1 text-[11px] text-[#6B7280]">Lower numbers appear first inside the category.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Price
                </Label>
                <Input
                  type="number"
                  value={form.price}
                  onChange={(e) =>
                    setForm({ ...form, price: parseFloat(e.target.value || 0) })
                  }
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Compare at price
                </Label>
                <Input
                  type="number"
                  value={form.compare_at_price}
                  onChange={(e) =>
                    setForm({ ...form, compare_at_price: e.target.value })
                  }
                  className="rounded-sm"
                  placeholder="Optional MRP"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Menu item images
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    [...(e.target.files || [])].forEach(uploadProductImage);
                    e.target.value = "";
                  }}
                  className="rounded-sm"
                  disabled={uploadingImage}
                />
                <div className="flex flex-wrap gap-2 mt-3">
                  {(form.image_urls || []).map((url, i) => (
                    <div
                      key={url + i}
                      className="relative w-16 h-16 rounded border overflow-hidden"
                    >
                      <img
                        src={resolveMediaUrl(url)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        className="absolute top-0 right-0 bg-black/60 text-white p-0.5"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            image_urls: prev.image_urls.filter(
                              (_, idx) => idx !== i,
                            ),
                          }))
                        }
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Description
                </Label>
                <Textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  className="rounded-sm"
                  rows={2}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Tags (comma separated)
                </Label>
                <Input
                  value={form.tags || ""}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="rounded-sm"
                  placeholder="pillows, bedding, cotton"
                />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Switch
                  checked={form.is_published !== false}
                  onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                />
                <Label>
                  {form.is_published !== false
                    ? "Available for ordering"
                    : "Sold out / over"}
                </Label>
              </div>
              <DialogFooter className="col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
                >
                  {editing ? "Save" : "Add"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={catOpen} onOpenChange={setCatOpen}>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingCat ? "Edit Category" : "New Category"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submitCategory} className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  required
                  value={catForm.name}
                  onChange={(e) =>
                    setCatForm({ ...catForm, name: e.target.value })
                  }
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label>Sort order</Label>
                <Input
                  type="number"
                  value={catForm.sort_order}
                  onChange={(e) =>
                    setCatForm({
                      ...catForm,
                      sort_order: parseInt(e.target.value || 0, 10),
                    })
                  }
                  className="rounded-sm mt-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={catForm.is_active !== false}
                  onCheckedChange={(v) =>
                    setCatForm({ ...catForm, is_active: v })
                  }
                />
                <Label>Active</Label>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCatOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#111827] text-white">
                  {editingCat ? "Save" : "Add"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!billOrder}
          onOpenChange={(open) => {
            if (!open) setBillOrder(null);
          }}
        >
          <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto rounded-sm p-0">
            <div className="print:hidden border-b border-[#E5E7EB] p-4">
              <DialogHeader>
                <DialogTitle className="text-xl font-extrabold">
                  Invoice preview
                </DialogTitle>
              </DialogHeader>
            </div>
            {billOrder && (
              <div>
                <div
                  id="mobile-invoice-print"
                  className="bg-white p-5 text-[#111827]"
                >
                  <div className="text-xl font-extrabold">
                    {me?.name || "Restaurant"}
                  </div>
                  <div className="mt-1 text-xs text-[#6B7280]">
                    Bill {billOrder.order_no || ""} -{" "}
                    {orderBillLabel(billOrder)}
                  </div>
                  <div className="mt-1 text-xs text-[#6B7280]">
                    {new Date().toLocaleString()}
                  </div>
                  <div className="mt-4 divide-y divide-[#E5E7EB]">
                    {(billOrder.items || []).map((item, index) => (
                      <div
                        key={item.line_id || index}
                        className="flex justify-between gap-3 py-2 text-sm"
                      >
                        <div>
                          <div className="font-medium">
                            {item.name || "Item"}
                          </div>
                          <div className="text-xs text-[#6B7280]">
                            Qty {item.qty || 0}
                          </div>
                        </div>
                        <div className="font-semibold">
                          ₹ {Number(item.line_total || 0).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                  {(() => {
                    const totals = invoiceTotals(billOrder, profile);
                    return (
                      <div className="mt-4 space-y-2 border-t border-[#111827] pt-3 text-sm">
                        {totals.gstEnabled && totals.gstPercent > 0 && (
                          <>
                            <div className="flex justify-between">
                              <span>Taxable amount</span>
                              <strong>₹ {totals.taxable.toFixed(2)}</strong>
                            </div>
                            <div className="flex justify-between">
                              <span>GST {totals.gstPercent}%</span>
                              <strong>₹ {totals.gstAmount.toFixed(2)}</strong>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between text-lg font-black">
                          <span>Total</span>
                          <span>₹ {totals.total.toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="mt-5 text-xs text-[#6B7280]">Thank you.</div>
                </div>
                <div className="print:hidden grid grid-cols-1 gap-2 border-t border-[#E5E7EB] p-4 sm:grid-cols-3">
                  <Button
                    type="button"
                    onClick={printCurrentBill}
                    className="rounded-sm bg-[#111827] text-white hover:bg-[#374151]"
                  >
                    <Printer className="mr-1.5 h-4 w-4" /> Print
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => shareBillToWhatsApp(billOrder)}
                    className="rounded-sm text-emerald-700"
                  >
                    <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={shareCurrentBill}
                    className="rounded-sm"
                  >
                    <Share2 className="mr-1.5 h-4 w-4" /> Share
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <TabsContent value="settings">
          <div className="restaurant-settings-dark bg-gradient-to-br from-[#111827] via-[#1F2937] to-[#0F172A] text-white rounded-2xl p-4 sm:p-6 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.9)] border border-white/10">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/60 mb-2">
                  Public ordering
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
                  Hotel / Restaurant settings
                </h3>
                <p className="mt-3 text-sm text-white/70 max-w-xl">
                  Configure public ordering details, billing tax, share link and
                  SEO settings from one place.
                </p>
              </div>
              <div className="flex items-center gap-3 bg-white/10 rounded-2xl px-4 py-3 border border-white/10">
                <Label className="text-xs text-white/70">Ordering open</Label>
                <Switch
                  checked={!!profile.is_open}
                  onCheckedChange={(v) =>
                    setProfile({ ...profile, is_open: v })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <Label className="text-xs uppercase tracking-wider text-white/60">
                  Headline
                </Label>
                <Input
                  value={profile.headline}
                  onChange={(e) =>
                    setProfile({ ...profile, headline: e.target.value })
                  }
                  className="rounded-lg bg-white/95 text-[#111827] mt-2"
                  placeholder="Premium home decor and bedding"
                />
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <Label className="text-xs uppercase tracking-wider text-white/60">
                  Working hours
                </Label>
                <Input
                  value={profile.hours}
                  onChange={(e) =>
                    setProfile({ ...profile, hours: e.target.value })
                  }
                  className="rounded-lg bg-white/95 text-[#111827] mt-2"
                  placeholder="Mon-Sat · 10 AM - 8 PM"
                />
              </div>
              {basicMenuLevel2 && <div className="md:col-span-2 rounded-xl border border-emerald-300/30 bg-emerald-300/10 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-200">Customer UPI payment</div>
                <p className="mt-1 text-[11px] text-white/60">Adds a Pay tab to the Basic Menu Level 2 customer page. The selected UPI app receives the current bill amount automatically.</p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div><Label className="text-xs text-white/70">Restaurant UPI ID</Label><Input value={profile.basic_menu_upi_id || ""} onChange={(e) => setProfile({ ...profile, basic_menu_upi_id: e.target.value })} placeholder="restaurant@bank" className="mt-1 bg-white/95 text-[#111827]" /></div>
                  <div><Label className="text-xs text-white/70">Payee name</Label><Input value={profile.basic_menu_upi_payee_name || ""} onChange={(e) => setProfile({ ...profile, basic_menu_upi_payee_name: e.target.value })} placeholder={me?.organization_name || "Restaurant name"} className="mt-1 bg-white/95 text-[#111827]" /></div>
                </div>
                <p className="mt-2 text-[10px] text-amber-100/80">Test a ₹1 payment before public use. The owner must verify receipt before confirming an order as paid.</p>
              </div>}
              <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-xl p-4">
                <Label className="text-xs uppercase tracking-wider text-white/60">
                  Store description
                </Label>
                <Textarea
                  value={profile.description}
                  onChange={(e) =>
                    setProfile({ ...profile, description: e.target.value })
                  }
                  className="rounded-lg bg-white/95 text-[#111827] mt-2"
                  rows={3}
                  placeholder="Tell customers about your store."
                />
              </div>
              <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-xl p-4">
                <Label className="text-xs uppercase tracking-wider text-white/60">
                  Announcement bar
                </Label>
                <Input
                  value={profile.announcement || ""}
                  onChange={(e) =>
                    setProfile({ ...profile, announcement: e.target.value })
                  }
                  className="rounded-lg bg-white/95 text-[#111827] mt-2"
                  placeholder="Free delivery on orders above ₹999 this week"
                />
                <p className="mt-1 text-[11px] text-white/50">
                  Shown as an orange strip below the store header on your public
                  page.
                </p>
              </div>
              <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-xl p-4">
                <Label className="text-xs uppercase tracking-wider text-white/60">
                  Cover image
                </Label>
                <div className="mt-3 flex items-center gap-4">
                  <div className="w-28 h-20 rounded-lg overflow-hidden bg-[#0F172A] flex items-center justify-center">
                    {profile.image_url ? (
                      <img
                        src={resolveMediaUrl(profile.image_url)}
                        alt="cover"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-xs text-white/60 px-2">No cover</div>
                    )}
                  </div>
                  <label
                    className={`inline-flex items-center gap-2 rounded-md px-3 py-2 bg-white/10 border border-white/10 text-sm cursor-pointer ${uploadingCover ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => uploadCoverImage(e.target.files?.[0])}
                      className="hidden"
                    />
                    <span>
                      {uploadingCover ? "Uploading…" : "Upload image"}
                    </span>
                  </label>
                </div>
              </div>
              {isBasicMenu && (
                <div className="md:col-span-2 rounded-xl border border-emerald-300/30 bg-emerald-300/10 p-4">
                  <div className="grid gap-5 md:grid-cols-[180px_1fr] md:items-center">
                    <div className="flex min-h-[180px] flex-col items-center justify-center rounded-lg bg-[#F7F3EA] p-3 text-center">
                      <div className="max-w-full truncate text-sm font-extrabold text-[#0F3D33]">{me?.organization_name || me?.name || "Restaurant"}</div>
                      <div className="mt-0.5 text-[9px] font-bold tracking-wider text-amber-700">SCAN TO VIEW OUR MENU</div>
                      {basicMenuQrUrl ? <img src={basicMenuQrUrl} alt="Menu-only QR" className="mt-2 h-28 w-28 bg-white object-contain p-1" /> : <QrCode className="mt-2 h-16 w-16 text-slate-400" />}
                      <div className="mt-1 text-[9px] text-slate-500">Menu & prices only</div>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-emerald-200">{basicMenuLevel2 ? "General menu QR" : "Menu-only QR"}</div>
                      <h4 className="mt-1 text-lg font-bold text-white">{basicMenuLevel2 ? "General QR with manual table entry" : "Print or download your read-only menu QR"}</h4>
                      <p className="mt-1 text-xs leading-5 text-white/60">{basicMenuLevel2 ? "This general QR still works and asks the guest for a table number. For automatic table identification, use the permanent table QR codes below." : "Customers can scan this QR to view menu items and prices without a table code, cart or ordering."}</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button type="button" onClick={printBasicMenuQr} disabled={!basicMenuQrUrl} className="rounded-md bg-white text-[#111827] hover:bg-white/90"><Printer className="mr-2 h-4 w-4" />Print QR</Button>
                        <Button type="button" variant="outline" onClick={downloadBasicMenuQrCard} disabled={!basicMenuQrUrl} className="rounded-md border-white/20 bg-white/10 text-white hover:bg-white/20"><Download className="mr-2 h-4 w-4" />Download menu card</Button>
                        <Button asChild type="button" variant="outline" disabled={!basicMenuUrl} className="rounded-md border-white/20 bg-white/10 text-white hover:bg-white/20"><a href={basicMenuUrl} target="_blank" rel="noreferrer"><ExternalLink className="mr-2 h-4 w-4" />View menu</a></Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {basicMenuLevel2 && <BasicMenuTableQrManager restaurantName={me?.organization_name || me?.name || "Restaurant"} />}
              <div className="md:col-span-2 rounded-xl border border-amber-300/30 bg-amber-300/10 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-200">Basic menu appearance only</div>
                <p className="mt-1 text-[11px] text-white/60">These options affect only the read-only Basic Menu QR page. Advanced ordering is unchanged.</p>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <div><Label className="text-xs text-white/70">Background color</Label><div className="mt-2 flex gap-2"><input type="color" value={profile.basic_menu_background_color || "#F7F3EA"} onChange={(e) => setProfile({ ...profile, basic_menu_background_color: e.target.value })} className="h-10 w-14 cursor-pointer rounded border border-white/20 bg-transparent" /><Input value={profile.basic_menu_background_color || "#F7F3EA"} onChange={(e) => setProfile({ ...profile, basic_menu_background_color: e.target.value })} className="bg-white/95 text-[#111827]" /></div></div>
                  <div>
  <Label className="text-xs text-white/70">Small top banner <span className="font-normal text-white/50">(1600 × 320 px; videos max 20 sec)</span></Label>
  <label className="mt-2 flex h-10 cursor-pointer items-center justify-center rounded border border-white/20 bg-white/10 text-xs font-semibold">
    <input type="file" accept="image/*,video/mp4,video/webm" className="hidden" onChange={(e) => { uploadBasicMenuBannerMedia(e.target.files?.[0]); e.target.value = ""; }} />
    {uploadingCover ? "Uploading…" : "Add image / video"}
  </label>
  <div className="mt-2 text-[10px] text-white/50">Maximum 2 videos. Each video can be up to 20 seconds.</div>
  {profile.basic_menu_banner_url && <div className="mt-2 flex items-center justify-between rounded border border-white/10 bg-white/5 p-2"><span className="truncate text-xs text-white/70">Banner image</span><button type="button" onClick={() => setProfile({ ...profile, basic_menu_banner_url: "" })} className="text-xs text-red-200">Remove</button></div>}
  {(profile.basic_menu_banner_videos || []).map((video, index) => <div key={video.id || video.url} className="mt-2 flex items-center gap-2 rounded border border-white/10 bg-white/5 p-2"><video src={resolveMediaUrl(video.url)} className="h-10 w-20 rounded object-cover" muted playsInline /><span className="min-w-0 flex-1 truncate text-xs text-white/70">Video {index + 1} · {Math.round(Number(video.duration_seconds || 0))} sec</span><button type="button" onClick={() => setProfile({ ...profile, basic_menu_banner_videos: (profile.basic_menu_banner_videos || []).filter((item) => item !== video) })} className="text-xs text-red-200">Remove</button></div>)}
</div>
                  <div><Label className="text-xs text-white/70">Image behind menu items</Label><label className="mt-2 flex h-10 cursor-pointer items-center justify-center rounded border border-white/20 bg-white/10 text-xs font-semibold"><input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBasicMenuImage(e.target.files?.[0], "basic_menu_item_background_url", "Menu item background")} />{profile.basic_menu_item_background_url ? "Replace image" : "Upload image"}</label>{profile.basic_menu_item_background_url && <button type="button" onClick={() => setProfile({ ...profile, basic_menu_item_background_url: "" })} className="mt-2 text-xs text-red-200">Remove image</button>}</div>
                </div>
                <div className="md:col-span-3 rounded-lg border border-white/10 bg-white/5 p-3">
                  <Label className="text-xs text-white/70">Offer ticker text</Label>
                  <Input value={profile.basic_menu_ticker_text || ""} onChange={(e) => setProfile({ ...profile, basic_menu_ticker_text: e.target.value })} placeholder="Today special offer..." className="mt-2 bg-white/95 text-[#111827]" />
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div><Label className="text-[11px] text-white/60">Background color</Label><div className="mt-1 flex gap-2"><input type="color" value={profile.basic_menu_ticker_background_color || "#0F3D33"} onChange={(e) => setProfile({ ...profile, basic_menu_ticker_background_color: e.target.value })} className="h-10 w-12 rounded border border-white/20" /><Input value={profile.basic_menu_ticker_background_color || "#0F3D33"} onChange={(e) => setProfile({ ...profile, basic_menu_ticker_background_color: e.target.value })} className="bg-white/95 text-[#111827]" /></div></div>
                    <div><Label className="text-[11px] text-white/60">Text color</Label><div className="mt-1 flex gap-2"><input type="color" value={profile.basic_menu_ticker_text_color || "#FFFFFF"} onChange={(e) => setProfile({ ...profile, basic_menu_ticker_text_color: e.target.value })} className="h-10 w-12 rounded border border-white/20" /><Input value={profile.basic_menu_ticker_text_color || "#FFFFFF"} onChange={(e) => setProfile({ ...profile, basic_menu_ticker_text_color: e.target.value })} className="bg-white/95 text-[#111827]" /></div></div>
                    <div><Label className="text-[11px] text-white/60">Font size (10–24 px)</Label><Input type="number" min="10" max="24" value={profile.basic_menu_ticker_font_size || 14} onChange={(e) => setProfile({ ...profile, basic_menu_ticker_font_size: Math.max(10, Math.min(24, Number(e.target.value || 14))) })} className="mt-1 bg-white/95 text-[#111827]" /></div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={profile.gst_enabled === true}
                      onCheckedChange={(v) =>
                        setProfile({ ...profile, gst_enabled: v })
                      }
                    />
                    <Label className="text-sm text-white">
                      Enable GST on printed bills
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs uppercase tracking-wider text-white/60">
                      GST %
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={profile.gst_percent || 0}
                      onChange={(e) =>
                        setProfile({ ...profile, gst_percent: e.target.value })
                      }
                      className="w-28 rounded-lg bg-white/95 text-[#111827]"
                    />
                  </div>
                </div>
              </div>
              <details className="md:col-span-2 bg-white/5 border border-white/10 rounded-xl p-4">
                <summary className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-white">
                  <Settings className="h-4 w-4" /> Share link and SEO settings
                </summary>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="md:col-span-2 rounded-lg border border-white/10 bg-white/5 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-white/60 mb-1">
                      Public ordering link
                    </div>
                    <div className="font-mono text-xs text-white/80 break-all">
                      {storeUrl || "Loading ordering link..."}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={copyStoreLink}
                      disabled={!storeUrl}
                      className="mt-3 rounded-xl bg-white/10 text-white border-white/15 hover:bg-white/20 disabled:opacity-50"
                    >
                      <Copy className="w-3.5 h-3.5 mr-2" /> Copy link
                    </Button>
                  </div>
                  <Input
                    value={seoForm.seo_title || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, seo_title: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="SEO title"
                  />
                  <Input
                    value={seoForm.meta_keywords || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, meta_keywords: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="Meta keywords"
                  />
                  <Textarea
                    value={seoForm.meta_description || ""}
                    onChange={(e) =>
                      setSeoForm({
                        ...seoForm,
                        meta_description: e.target.value,
                      })
                    }
                    className="rounded-lg bg-white/95 text-[#111827] md:col-span-2"
                    rows={2}
                    placeholder="Meta description"
                  />
                  <Input
                    value={seoForm.canonical_url || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, canonical_url: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="Canonical URL"
                  />
                  <Input
                    value={seoForm.og_image_url || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, og_image_url: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="OG image URL"
                  />
                  <Input
                    value={seoForm.business_address || ""}
                    onChange={(e) =>
                      setSeoForm({
                        ...seoForm,
                        business_address: e.target.value,
                      })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="Business address"
                  />
                  <Input
                    value={seoForm.google_map_url || ""}
                    onChange={(e) =>
                      setSeoForm({ ...seoForm, google_map_url: e.target.value })
                    }
                    className="rounded-lg bg-white/95 text-[#111827]"
                    placeholder="Google Map URL"
                  />
                  <Button
                    type="button"
                    onClick={saveSeoSettings}
                    disabled={seoBusy}
                    className="rounded-xl bg-white text-[#111827] hover:bg-white/90 md:col-span-2"
                  >
                    {seoBusy ? "Saving SEO..." : "Save SEO settings"}
                  </Button>
                </div>
              </details>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-6 border-t border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-white/60 mb-1">
                  Save settings
                </div>
                <div className="text-xs text-white/70">
                  Share and SEO are inside the settings section above.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={saveProfile}
                  className="rounded-xl bg-white text-[#111827] hover:bg-white/90"
                >
                  Save hotel settings
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SocietyPanel() {
  const [rooms, setRooms] = useState([]);
  const [roomOpen, setRoomOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const emptyRoom = { room_no: "", user_name: "", mobile: "", notes: "" };
  const [roomForm, setRoomForm] = useState(emptyRoom);

  const [notices, setNotices] = useState([]);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const emptyNotice = { title: "", body: "", image_url: "" };
  const [noticeForm, setNoticeForm] = useState(emptyNotice);
  const [uploadingNotice, setUploadingNotice] = useState(false);

  const uploadNoticeImage = async (file) => {
    if (!file) return;
    setUploadingNotice(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "storefront");
      fd.append("zone", "main");
      fd.append("name", file.name);
      const { data } = await api.post("/client/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNoticeForm((prev) => ({
        ...prev,
        image_url: data.public_url || data.url || "",
      }));
      toast.success("Notice image uploaded");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setUploadingNotice(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [r, n] = await Promise.all([
        api.get("/client/rooms"),
        api.get("/client/notices"),
      ]);
      setRooms(r.data);
      setNotices(n.data);
    } catch {}
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const submitRoom = async (e) => {
    e.preventDefault();
    try {
      if (editingRoom)
        await api.put(`/client/rooms/${editingRoom.id}`, roomForm);
      else await api.post("/client/rooms", roomForm);
      toast.success(editingRoom ? "Room updated" : "Room added");
      setRoomOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const removeRoom = async (id) => {
    if (!window.confirm("Delete this room?")) return;
    try {
      await api.delete(`/client/rooms/${id}`);
      toast.success("Deleted");
      load();
    } catch {}
  };

  const submitNotice = async (e) => {
    e.preventDefault();
    try {
      if (editingNotice)
        await api.put(`/client/notices/${editingNotice.id}`, noticeForm);
      else await api.post("/client/notices", noticeForm);
      toast.success(editingNotice ? "Notice updated" : "Notice uploaded");
      setNoticeOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };

  const removeNotice = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await api.delete(`/client/notices/${id}`);
      toast.success("Deleted");
      load();
    } catch {}
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] mb-1">
              Society directory
            </div>
            <h3 className="font-display text-2xl font-extrabold tracking-tight">
              Rooms and residents
            </h3>
          </div>
          <Button
            onClick={() => {
              setEditingRoom(null);
              setRoomForm(emptyRoom);
              setRoomOpen(true);
            }}
            className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Room
          </Button>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                  Room
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                  Resident
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                  Mobile
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                  Notes
                </TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rooms.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-10 text-sm text-[#6B7280]"
                  >
                    No rooms yet.
                  </TableCell>
                </TableRow>
              )}
              {rooms.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-display font-bold">
                    {r.room_no}
                  </TableCell>
                  <TableCell>{r.user_name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.mobile || "-"}
                  </TableCell>
                  <TableCell className="text-sm text-[#6B7280]">
                    {r.notes}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setEditingRoom(r);
                        setRoomForm({ ...emptyRoom, ...r });
                        setRoomOpen(true);
                      }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeRoom(r.id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280] mb-1">
              Notice upload
            </div>
            <h3 className="font-display text-2xl font-extrabold tracking-tight">
              Society announcements
            </h3>
          </div>
          <Button
            onClick={() => {
              setEditingNotice(null);
              setNoticeForm(emptyNotice);
              setNoticeOpen(true);
            }}
            className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
          >
            <Megaphone className="w-4 h-4 mr-2" /> Add Notice
          </Button>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                  Title
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                  Message
                </TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
                  Image
                </TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notices.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center py-10 text-sm text-[#6B7280]"
                  >
                    No notices yet.
                  </TableCell>
                </TableRow>
              )}
              {notices.map((n) => (
                <TableRow key={n.id}>
                  <TableCell className="font-semibold">{n.title}</TableCell>
                  <TableCell className="text-sm text-[#6B7280] max-w-[420px] truncate">
                    {n.body}
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {n.image_url ? "Added" : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setEditingNotice(n);
                        setNoticeForm({ ...emptyNotice, ...n });
                        setNoticeOpen(true);
                      }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeNotice(n.id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={roomOpen} onOpenChange={setRoomOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
              {editingRoom ? "Edit Room" : "Add Room"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitRoom} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Room number
              </Label>
              <Input
                value={roomForm.room_no}
                onChange={(e) =>
                  setRoomForm({ ...roomForm, room_no: e.target.value })
                }
                required
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Resident name
              </Label>
              <Input
                value={roomForm.user_name}
                onChange={(e) =>
                  setRoomForm({ ...roomForm, user_name: e.target.value })
                }
                required
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Mobile
              </Label>
              <Input
                value={roomForm.mobile}
                onChange={(e) =>
                  setRoomForm({ ...roomForm, mobile: e.target.value })
                }
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Notes
              </Label>
              <Textarea
                value={roomForm.notes}
                onChange={(e) =>
                  setRoomForm({ ...roomForm, notes: e.target.value })
                }
                className="rounded-sm"
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRoomOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
              >
                {editingRoom ? "Save" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={noticeOpen} onOpenChange={setNoticeOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
              {editingNotice ? "Edit Notice" : "Upload Notice"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitNotice} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Title
              </Label>
              <Input
                value={noticeForm.title}
                onChange={(e) =>
                  setNoticeForm({ ...noticeForm, title: e.target.value })
                }
                required
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Message
              </Label>
              <Textarea
                value={noticeForm.body}
                onChange={(e) =>
                  setNoticeForm({ ...noticeForm, body: e.target.value })
                }
                className="rounded-sm"
                rows={3}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                Notice image
              </Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => uploadNoticeImage(e.target.files?.[0])}
                className="rounded-sm"
              />
              <div className="mt-2 text-xs text-[#6B7280]">
                Uploads into your storefront folder in media.
              </div>
              <div className="mt-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">
                  Image URL
                </Label>
                <Input
                  value={noticeForm.image_url}
                  onChange={(e) =>
                    setNoticeForm({ ...noticeForm, image_url: e.target.value })
                  }
                  className="rounded-sm"
                  placeholder="https://..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setNoticeOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-sm bg-[#111827] hover:bg-[#374151] text-white"
              >
                {editingNotice ? "Save" : "Upload"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ClientStorefront() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    api
      .get("/client/me")
      .then((r) => setMe(r.data))
      .catch(() => {});
  }, []);

  const v = me?.vertical || "general";
  const config = useMemo(
    () => ({
      doctor: {
        overline: "Storefront / Doctor",
        title: "Clinic console.",
        subtitle: "Manage profile, accept bookings and call tokens.",
        icon: Stethoscope,
        panel: (
          <ServicePanel vertical="doctor" label="Doctor" icon={Stethoscope} />
        ),
      },
      salon: {
        overline: "Storefront / Salon",
        title: "Salon console.",
        subtitle:
          "Publish services, collect bookings and run live token queue.",
        icon: Scissors,
        panel: <ServicePanel vertical="salon" label="Salon" icon={Scissors} />,
      },
      retailer: {
        overline: "Hotel / Restaurant",
        title: "Digital menu & orders.",
        subtitle:
          "Manage menu items, tables, waiter routing, kitchen status and customer orders.",
        icon: Package,
        panel: <HotelRestaurantPanel withPageHeader />,
      },
      retail_store: {
        overline: "Storefront / Retail Store",
        title: "Retail store & signage.",
        subtitle:
          "Manage signage-first retail clients, optional ecommerce catalog, SEO, offers and customer enquiries.",
        icon: ShoppingCart,
        panel: <RetailStorefrontPanel />,
      },
      society: {
        overline: "Storefront / Society",
        title: "Residence registry.",
        subtitle:
          "Manage owner directory, visitors, approvals, signage directory zones and voice display prompts.",
        icon: Building,
        panel: <HousingSocietyPanel />,
      },
      general: {
        overline: "Storefront / Academy",
        title: "Courses & enrollments.",
        subtitle:
          "Run tuition centers, academies, coaching, and training programs with a professional public page.",
        icon: GraduationCap,
        panel: <GeneralStorefrontPanel />,
      },
      ad_agency: {
        overline: "Storefront / Ad Agency",
        title: "Advertising storefront.",
        subtitle:
          "Manage ad packages, gallery placements, campaign slot enquiries and WhatsApp leads.",
        icon: Megaphone,
        panel: <AdAgencyStorefrontPanel />,
      },
    }),
    [],
  );

  const screen = config[v] || config.general;

  return (
    <div
      data-testid="client-storefront"
      className={v === "retailer" ? "restaurant-excel-theme" : ""}
    >
      {v !== "retailer" && v !== "salon" && (
        <PageHeader
          overline={screen.overline}
          title={screen.title}
          subtitle={screen.subtitle}
        />
      )}
      {screen.panel}
    </div>
  );
}
